import React, { Component } from "react";
import {
  company,
  loading,
  NavbarAdmin,
  Modal,
  Select,
  DatePicker,
  DateRangeFields,
  currentLang,
  Pagination,
  alert,
  GET,
  POST,
  PUT,
  DELETE,
  UPLOAD,
  t,
  searchCustomerRows,
  mergeCustomerPins,
  addCustomerPin,
  CUSTOMER_SEARCH_PAGE_SIZE,
} from "../../components/CustomComponent.js";
import { can } from "../../utils/permission.js";
import { formatFileSize, formatText } from "../../utils/fileUpload";
import { formatNumber } from "../../utils/formatting";

/**
 * แผนงานตามสัญญา (เมนูกลุ่ม PM) — เชื่อม API จริง (admin/contract-plans)
 * - ปฏิทิน / ตาราง / มุมมองโครงการ ใช้ข้อมูลชุดเดียวกันจาก admin/contract-plans/calendar (ช่วง = เดือนที่แสดง)
 * - โครงการ / กรอบวันที่ / ผู้ปฏิบัติงาน / สถานะ HOLD มาจาก Project Master (admin/contract-plans/projects)
 * - ลูกค้า = ลูกค้าจาก master (ค้นหาฝั่ง server ไม่โหลดทั้งก้อน) ฟิลเตอร์ใช้ admin/customers (ทุกราย) ส่วนฟอร์มใช้ admin/contract-plans/customers
 *   (เฉพาะลูกค้าที่มีโครงการ active ของประเภทที่เลือก) โครงการของลูกค้า = owner ของ SAP ตรงรหัสลูกค้า
 *   หรือทรัพย์สินของลูกค้าผูกโครงการไว้ (แบบเดียวกับเมนูใบคำร้อง) - server กรองให้ผ่าน customer_id ฟอร์มจึงโหลดเฉพาะโครงการของลูกค้าที่เลือก
 *   โครงการที่ยังไม่วางแผน (หลายร้อยแถว) โหลดเฉพาะตอนกดแสดง
 * - หัวข้องาน + งานย่อยจาก admin/work-topics, เทมเพลตเช็คลิสต์จาก admin/checklist-templates
 * - งานติดตั้ง = งวดงาน (หัวข้องาน + ช่วงวันที่ + งานย่อย + ผู้ปฏิบัติงาน)
 * - งานดูแลสระ = 1 แผนต่อ (ลูกค้า + โครงการ) แบ่งเป็นแผนย่อย "แผนที่ 1, 2, ..." ต่อเนื่องกันในกรอบวันที่โครงการ
 *   แต่ละแผนย่อยมีวันเข้าปฏิบัติงาน + เทมเพลตเช็คลิสต์ของตัวเอง (วันเริ่มระบบกำหนด แก้ได้เฉพาะวันสิ้นสุด)
 */

const I = (code) => String.fromCharCode(code);
const IC = {
  plan: I(0xf0ae),
  edit: I(0xf044),
  sync: I(0xf2f1), // รอบงานประจำ (recurring)
  calendar: I(0xf073),
  table: I(0xf0ce),
  project: I(0xf1b3),
  plus: I(0xf067),
  search: I(0xf002),
  chevL: I(0xf053),
  chevR: I(0xf054),
  chevD: I(0xf078),
  chkCircle: I(0xf058),
  hourglass: I(0xf254),
  warning: I(0xf071),
  pause: I(0xf04c),
  info: I(0xf05a),
  check: I(0xf00c),
  trash: I(0xf1f8),
  file: I(0xf15c),
  users: I(0xf0c0),
  clipboard: I(0xf46c),
  save: I(0xf0c7),
  times: I(0xf00d),
  building: I(0xf1ad),
};

// ประเภทแผน = process ของโครงการ (ค่าตรงกับ backend project.constants.js)
const PLAN_TYPES = ["installation", "pool_maintenance"];
const TYPE_STYLE = {
  installation: { bar: "#2563eb", bg: "#EAF2FE", text: "#1e40af", cell: "#dbe7fd" },
  pool_maintenance: { bar: "#0ea5e9", bg: "#E0F2FE", text: "#0369a1", cell: "#cdf1fb" },
  hold: { bar: "#94a3b8", bg: "#e2e8f0", text: "#64748b", cell: "#e2e8f0" },
};
// สถานะรอบงาน (ตรงกับ backend contract-plan.service.js) - เริ่มต้น waiting (รอเข้าปฏิบัติงาน) ทุกรอบ, done = เช็คอินแล้ว
const STATUS_STYLE = {
  done: { labelKey: "cp_status_done", color: "#16a34a", bg: "#e8f6ee", icon: IC.chkCircle },
  in_progress: { labelKey: "cp_status_in_progress", color: "#2563eb", bg: "#eaf1ff", icon: IC.hourglass },
  waiting: { labelKey: "cp_status_waiting", color: "#d97706", bg: "#fdf3e4", icon: IC.hourglass },
  hold: { labelKey: "cp_status_hold", color: "#c2410c", bg: "#fff4ec", icon: IC.pause },
};
const TABLE_PAGE_SIZE = 20;
// เซลล์ข้อความในมุมมองตาราง: แสดงสูงสุด 2 บรรทัด เกินนั้นตัดเป็น ... (ชี้เมาส์เห็นข้อความเต็มจาก title)
const CELL_ELLIPSIS = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word" };
// ปุ่มวันประจำสัปดาห์เรียง จ.-อา. แต่ค่าที่เก็บ = 0 (อาทิตย์) ... 6 (เสาร์)
// ปุ่มวันประจำและหัวคอลัมน์มุมมองโครงการใช้ตัวย่อ (จ. อ. ...) ส่วนหัวปฏิทินใช้ชื่อเต็ม
const DAY_PILLS = [
  { dow: 1, key: "mon" }, { dow: 2, key: "tue" }, { dow: 3, key: "wed" }, { dow: 4, key: "thu" },
  { dow: 5, key: "fri" }, { dow: 6, key: "sat" }, { dow: 0, key: "sun" },
];
const WEEKDAY_SHORT_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const WEEKDAY_KEYS = ["svp_sun", "svp_mon", "svp_tue", "svp_wed", "svp_thu", "svp_fri", "svp_sat"];
const MONTH_KEYS = ["svp_month_jan", "svp_month_feb", "svp_month_mar", "svp_month_apr", "svp_month_may", "svp_month_jun", "svp_month_jul", "svp_month_aug", "svp_month_sep", "svp_month_oct", "svp_month_nov", "svp_month_dec"];
const DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";
const DOC_MAX_SIZE = 10 * 1024 * 1024;
const UPLOAD_FOLDER = "contract-plans/documents";

const isEN = () => localStorage.getItem("lang") === "EN";
const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parseIso = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (iso, n) => {
  const d = parseIso(iso);
  d.setDate(d.getDate() + n);
  return isoOf(d);
};
const startOfMonth = (iso) => `${iso.slice(0, 7)}-01`;
const endOfMonth = (iso) => {
  const [y, m] = iso.split("-").map(Number);
  return isoOf(new Date(y, m, 0));
};
// yyyy-mm-dd -> dd/mm/yyyy (ค.ศ. ทุกภาษา)
const fmtDate = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const yearLabel = (y) => y;
const typeLabel = (type) => (PLAN_TYPES.includes(type) ? t(`project_type_${type}`) : "-");
// ชื่อช่างตามภาษาที่ใช้อยู่ (ไม่ต่อรหัส) - รูปแบบเดียวกับหน้าโครงการ
const technicianName = (tech) => {
  if (!tech) return "";
  const en = isEN();
  const first = en ? tech.first_name_en || tech.first_name_th : tech.first_name_th || tech.first_name_en;
  const last = en ? tech.last_name_en || tech.last_name_th : tech.last_name_th || tech.last_name_en;
  const nick = en ? tech.nickname_en || tech.nickname_th : tech.nickname_th || tech.nickname_en;
  return [first, last].filter(Boolean).join(" ") || nick || tech.technician_code || tech.uid;
};
// ป้ายช่างในฟอร์ม: รหัส - ชื่อ (รูปแบบเดียวกับหน้าโครงการ) ส่วนปฏิทิน/ตารางใช้ชื่ออย่างเดียว
const technicianCodeName = (tech) => [tech?.technician_code, technicianName(tech)].filter(Boolean).join(" - ");
const projectLabel = (p) => [p.project_code, p.name].filter(Boolean).join(" — ");
const ownerLabel = (p) => [p.owner_code, p.owner_name].filter(Boolean).join(" - ") || t("cp_owner_unspecified");
// ตัวเลือกลูกค้า: value = uid ของลูกค้า ป้าย "รหัส ชื่อ" ตามภาษา (รูปแบบเดียวกับเมนูใบคำร้อง)
const toCustomerOption = (c) => ({
  value: c.uid,
  label: `${c.customer_code || ""} ${isEN() ? c.full_name_en || c.full_name_th || "-" : c.full_name_th || c.full_name_en || "-"}`.trim(),
});
// เจ้าของแผน = ลูกค้าที่บันทึกไว้กับแผน - แผนเก่าที่ยังไม่มีลูกค้าใช้เจ้าของโครงการจาก SAP แทน
const planOwnerLabel = (project, plan) => (plan?.customer ? toCustomerOption(plan.customer).label : ownerLabel(project || {}));
const isHold = (p) => p?.status === "hold";
// ---------- แผนย่อยของงานดูแลสระ ----------
// แผนย่อยใหม่ไม่เลือกวันเข้าปฏิบัติงานประจำ / เทมเพลตเช็คลิสต์ไว้ล่วงหน้า - ผู้ใช้เลือกเอง (บันทึกไม่ได้จนกว่าจะเลือกครบ)
const MONTH_SHORT_KEYS = ["jan_short", "feb_short", "mar_short", "apr_short", "may_short", "jun_short", "jul_short", "aug_short", "sep_short", "oct_short", "nov_short", "dec_short"];
const daysBetween = (startIso, endIso) => Math.round((parseIso(endIso) - parseIso(startIso)) / 86400000);
const minIso = (...values) => values.filter(Boolean).sort()[0] || "";
const maxIso = (...values) => values.filter(Boolean).sort().slice(-1)[0] || "";
const emptySegment = (patch = {}) => ({
  key: `${Date.now()}-${Math.random()}`,
  uid: null,
  end_date: "",
  days: [],
  checklist_id: "",
  is_active: true,
  inactive_from: null,
  has_checkin: false,
  checkin_count: 0,
  first_checkin_date: null,
  last_checkin_date: null,
  ...patch,
});
// วันเริ่มของแต่ละแผนย่อย (ระบบกำหนด): แผนที่ 1 = วันเริ่มโครงการ, แผนถัดไป = วันถัดจากวันสิ้นสุดของแผนก่อนหน้า
const segmentStarts = (project, segments) => {
  let start = project?.range_start_date || "";
  return segments.map((seg) => {
    const current = start;
    start = seg.end_date ? addDays(seg.end_date, 1) : "";
    return current;
  });
};
// นับรอบเข้าพบ: ทุกวันในช่วงที่ตรงกับวันเข้าที่เลือก = 1 รอบ (แผนที่ปิดแล้วไม่นับตั้งแต่วันที่ปิด) แยกรายเดือนสำหรับแท็กตัวอย่างตารางงาน
const countRounds = (start, end, days, stopFrom = null) => {
  const months = [];
  let total = 0;
  if (!start || !end || end < start) return { total, months };
  const selected = new Set(days);
  for (let iso = start; iso <= end; iso = addDays(iso, 1)) {
    const ym = iso.slice(0, 7);
    if (!months.length || months[months.length - 1].ym !== ym) months.push({ ym, count: 0 });
    if (selected.has(parseIso(iso).getDay()) && !(stopFrom && iso >= stopFrom)) {
      months[months.length - 1].count += 1;
      total += 1;
    }
  }
  return { total, months };
};
const monthShortLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return `${t(MONTH_SHORT_KEYS[m - 1])} ${yearLabel(y)}`;
};

// locked = งวดที่ถึงวันสิ้นสุดแล้วและมีการบันทึกการทำงานแล้ว (แก้ไขไม่ได้ทั้งงวด) - ถึงวันสิ้นสุดแล้วแต่ยังไม่มีการบันทึก = แก้ได้ทุกอย่าง
// has_work = มีการบันทึกการทำงานในงวดแล้ว (ลบไม่ได้ แก้วันที่เริ่มไม่ได้ วันที่สิ้นสุดต้องหลัง last_work_date) - ตั้งตอนโหลดแผนเดิม
const emptyPhase = (start = "", end = "") => ({ key: `${Date.now()}-${Math.random()}`, uid: null, work_topic_ids: [], start_date: start, end_date: end, work_topic_item_ids: [], technician_ids: [], worker_open: false, worker_search: "", locked: false, has_work: false, work_count: 0, last_work_date: null, saved_end_date: null, collapsed: false });

export default class ContractPlan extends Component {
  constructor(props) {
    super(props);
    const todayIso = isoOf(new Date());
    this.state = {
      view: "calendar", // calendar | table | project
      cal_anchor: startOfMonth(todayIso),
      today: todayIso,
      projects: [],
      occurrences: [],
      summary: {},

      // ตัวกรอง (มีผลทุกมุมมอง) - job_type อยู่ข้างปุ่มสลับมุมมอง ส่วนที่เหลืออยู่ในแถบค้นหา
      filter_search: "",
      filter_technician: "",
      filter_owner: "",
      filter_status: "",
      filter_date_start: "", // ช่วงวันที่ = ดูเฉพาะรอบงานในช่วง (ทุกมุมมอง) และเลื่อนปฏิทินไปเดือนของวันเริ่ม
      filter_date_end: "",
      job_type: "all",

      table_status: "all",
      table_page: 1,
      show_unplanned: false,
      day_modal_date: "", // มุมมองปฏิทิน: กด "+N รายการ" = เปิดรายการงานทั้งหมดของวันนั้น
      day_modal_search: "", // ข้อความที่กำลังพิมพ์
      day_modal_query: "", // คำค้นที่ใช้กรองจริง - ตามหลังการพิมพ์เล็กน้อย (หน่วงไว้ให้รายการไม่กระพริบทุกตัวอักษร)

      // master
      data_technicians: [],
      data_work_topics: [],
      data_checklists: [],
      data_customers: [], // ผลค้นหาลูกค้า (server) + ลูกค้าที่ปักหมุดไว้
      customer_pins: [],
      form_customers: [], // ตัวเลือกลูกค้าในฟอร์ม: เฉพาะลูกค้าที่มีโครงการ active ของประเภทที่เลือก (admin/contract-plans/customers)
      project_options_by_key: {}, // "<plan_type>|<customer uid>" -> โครงการของลูกค้านั้น
      customer_project_types: {}, // customer uid -> จำนวนโครงการแยกตามประเภท (ไว้บอกเหตุผลเมื่อประเภทที่เลือกไม่มีโครงการ)

      // ฟอร์ม
      modal_show: false,
      modal_edit: false,
      modal_view: false, // เปิดจากรอบงานที่สำเร็จแล้ว = ดูได้อย่างเดียว (มีแต่ปุ่มปิด)
      saving: false,
      f_plan_uid: "",
      f_plan_type: "",
      f_owner: "", // uid ลูกค้า
      f_project_id: "",
      f_project: null, // โครงการที่เปิดจากแถว/แผนเดิม (ไม่ต้องรู้ลูกค้า) - ใช้เมื่อไม่มีในตัวเลือกที่โหลดตามลูกค้า
      f_project_locked: false, // เปิดจากแถวโครงการ -> ล็อกประเภท/โครงการเหมือนโหมดแก้ไข (ลูกค้ายังต้องเลือกจากลูกค้าของโครงการนั้น)
      f_customer: null, // เจ้าของแผนที่บันทึกไว้ (โหมดแก้ไข) - มีแล้วช่องลูกค้าล็อก
      f_phases: [],
      f_segments: [], // งานดูแลสระ: แผนย่อย (แผนที่ 1, 2, ...)
      f_notes: "",
      f_documents: [],
    };
  }

  componentDidMount() {
    loading(false);
    this.loadMasters();
    this.SearchCustomers("");
    this.loadCalendar();
    // ลิงก์จากหน้ารายงานใกล้หมดประกัน: ?uid=<contract plan uid> -> เปิดแผนนั้นทันที
    const deepUid = new URLSearchParams(window.location.search).get("uid");
    // เอา ?uid= ออกจาก URL (คง param อื่น) กัน popup เด้งกลับตอนรีเฟรช
    if (deepUid) {
      const url = new URL(window.location.href);
      url.searchParams.delete("uid");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    if (deepUid) this.loadPlanIntoForm(deepUid);
  }

  // ---------- ลูกค้า: ค้นหาฝั่ง server ตอนพิมพ์ (Select เรียกผ่าน onSearch) - token กันผลที่ตอบช้ามาทับคำค้นล่าสุด ----------
  customerSearchToken = 0;
  SearchCustomers = async (keyword = "") => {
    const token = ++this.customerSearchToken;
    const rows = await searchCustomerRows(keyword, { search_scope: "display" });
    if (token !== this.customerSearchToken || rows === null) return;
    this.setState((prev) => ({ data_customers: mergeCustomerPins(rows.map(toCustomerOption), prev.customer_pins) }));
  };
  // ลูกค้าในฟอร์ม: ค้นหาฝั่ง server เฉพาะลูกค้าที่มีโครงการ active ของประเภทงานที่เลือก (โครงการ HOLD ไม่นับ)
  formCustomerSearchToken = 0;
  SearchFormCustomers = async (keyword = "") => {
    const token = ++this.formCustomerSearchToken;
    const planType = this.state.f_plan_type;
    if (!planType) return this.setState({ form_customers: [] });
    // โครงการถูกล็อกไว้แล้ว (แก้ไขแผน / เปิดจากแถวโครงการ) -> เหลือเฉพาะลูกค้าของโครงการนั้น
    const projectUid = this.state.modal_edit || this.state.f_project_locked ? this.state.f_project_id : "";
    const res = await GET("admin/contract-plans/customers", {
      plan_type: planType,
      data_search: keyword || "",
      page_size: CUSTOMER_SEARCH_PAGE_SIZE,
      ...(projectUid ? { project_id: projectUid } : {}),
    });
    if (token !== this.formCustomerSearchToken || !res?.status || planType !== this.state.f_plan_type) return;
    this.setState({ form_customers: this.toArray(res.data).map(toCustomerOption) });
  };
  // ลูกค้าที่เลือกไว้ต้องอยู่ในตัวเลือกเสมอ แม้ผลค้นหาล่าสุดจะไม่มีรายนั้น (Select หาป้ายจาก options)
  formCustomerOptions = () => {
    const { form_customers, customer_pins, f_owner } = this.state;
    return mergeCustomerPins(form_customers, customer_pins.filter((pin) => pin.value === f_owner));
  };

  pinCustomer = (option) => {
    if (!option?.value) return;
    this.setState((prev) => {
      const customer_pins = addCustomerPin(prev.customer_pins, { value: option.value, label: option.label });
      return { customer_pins, data_customers: mergeCustomerPins(prev.data_customers, customer_pins) };
    });
  };

  // ============================================================
  // โหลดข้อมูล
  // ============================================================
  toArray = (v) => (Array.isArray(v) ? v : []);

  loadMasters = async () => {
    const [resTech, resTopics, resChecklists] = await Promise.all([
      GET("admin/technicians", { page: 0, page_size: 1000, lite: "true", is_active: "true" }),
      GET("admin/work-topics", { page: 0, page_size: 1000 }),
      GET("admin/checklist-templates", { page: 0, page_size: 1000 }),
    ]);
    this.setState({
      data_technicians: this.toArray(resTech?.data),
      data_work_topics: this.toArray(resTopics?.data),
      data_checklists: this.toArray(resChecklists?.data),
    });
  };

  calendarParams = () => {
    const { cal_anchor, job_type, filter_search, filter_technician, filter_owner, filter_status, filter_date_start, filter_date_end } = this.state;
    return {
      // ใส่ปลายเดียว = ทั้งเดือนของวันที่นั้น · ไม่ใส่เลย = เดือนของ cal_anchor
      start: filter_date_start || (filter_date_end ? startOfMonth(filter_date_end) : startOfMonth(cal_anchor)),
      end: filter_date_end || (filter_date_start ? endOfMonth(filter_date_start) : endOfMonth(cal_anchor)),
      ...(job_type !== "all" ? { plan_type: job_type } : {}),
      ...(filter_search.trim() ? { data_search: filter_search.trim() } : {}),
      ...(filter_technician ? { technician_id: filter_technician } : {}),
      ...(filter_owner ? { customer_id: filter_owner } : {}),
      ...(filter_status ? { status: filter_status } : {}),
      // แถวโครงการที่ยังไม่วางแผนมีหลายร้อยแถว - ขอเฉพาะตอนเปิดดูในมุมมองโครงการ (จำนวนได้จาก summary เสมอ)
      include_unplanned: this.state.show_unplanned ? "true" : "false",
    };
  };

  loadCalendar = async () => {
    loading(true);
    const token = (this.calendarToken = (this.calendarToken || 0) + 1);
    const res = await GET("admin/contract-plans/calendar", this.calendarParams());
    if (token !== this.calendarToken) return;
    loading(false);
    if (!res?.status) {
      alert("", "warning", t("warning"), res?.message || t("fail"));
      return;
    }
    this.setState({
      projects: this.toArray(res.data?.projects),
      occurrences: this.toArray(res.data?.occurrences),
      summary: res.data?.summary || {},
      today: res.data?.today || this.state.today,
      table_page: 1,
    });
  };

  // โครงการเดียวพร้อมช่าง/กรอบวันที่ (เปิดฟอร์มจากแถวโครงการ)
  loadProjectRow = async (projectUid) => {
    const res = await GET("admin/contract-plans/projects", { project_id: projectUid });
    return this.toArray(res?.data)[0] || null;
  };

  // ตัวเลือกโครงการของฟอร์ม: เฉพาะประเภทงาน (installation | pool_maintenance) + ลูกค้าที่เลือก (แคชต่อคู่ โหลดใหม่หลังบันทึกเพื่ออัปเดต has_plan) - plan_count นับเฉพาะแผนของลูกค้านี้ (มีแผนแล้ว = ตัวเลือกสีเทา)
  projectOptionsKey = (planType, customerUid) => `${planType || ""}|${customerUid || ""}`;
  loadProjectOptions = async (planType, customerUid, force = false) => {
    if (!planType || !customerUid) return [];
    const key = this.projectOptionsKey(planType, customerUid);
    const cached = this.state.project_options_by_key[key];
    if (cached && !force) return cached;
    const res = await GET("admin/contract-plans/projects", { plan_type: planType, customer_id: customerUid });
    const list = this.toArray(res?.data);
    this.setState((prev) => ({ project_options_by_key: { ...prev.project_options_by_key, [key]: list } }));
    return list;
  };

  // ประเภทงานโครงการ: ค่าคงที่ 2 แบบตาม process ของโครงการ (งานติดตั้ง / งานดูแลสระ)
  planTypeOptions = () => PLAN_TYPES.map((type) => ({ value: type, label: typeLabel(type) }));

  // ============================================================
  // ตัวช่วยอ่านข้อมูลปฏิทิน
  // ============================================================
  // โครงการมีได้หลายแผน - API ส่งมา 1 แถวต่อแผน จึงรวมเป็นโครงการเดียวพร้อม plans[] (plan = แผนแรก ไว้ให้โค้ดเดิมใช้)
  projectMap = () => {
    const map = {};
    this.state.projects.forEach((p) => {
      if (!map[p.uid]) map[p.uid] = { ...p, plans: [] };
      if (p.plan) map[p.uid].plans.push(p.plan);
      if (!map[p.uid].plan && p.plan) map[p.uid].plan = p.plan;
    });
    return map;
  };

  technicianNameMap = () => {
    const map = {};
    this.state.data_technicians.forEach((tech) => { map[tech.uid] = technicianName(tech); });
    this.state.projects.forEach((p) => (p.technicians || []).forEach((tech) => { map[tech.uid] = technicianName(tech); }));
    return map;
  };

  occurrenceNote = (occ) => {
    if (occ.plan_type === "installation") {
      const topicNames = (occ.work_topics?.length ? occ.work_topics : occ.work_topic ? [occ.work_topic] : []).map((x) => x.name).filter(Boolean).join(", ");
      return `${t("cp_phase")} ${occ.phase_sequence || "-"}: ${topicNames || "-"}`;
    }
    return [t("cp_recurring_weekly"), occ.segment_sequence ? `${t("cp_plan_no")} ${occ.segment_sequence}` : "", occ.checklist_template?.name || ""].filter(Boolean).join(" • ");
  };

  occurrencesByDate = () => {
    const map = {};
    this.state.occurrences.forEach((occ) => { (map[occ.date] ||= []).push(occ); });
    return map;
  };

  // ============================================================
  // ตัวกรอง / ปฏิทิน
  // ============================================================
  // เลื่อนเดือน = ยกเลิกตัวกรองวันที่ (ตัวกรองวันที่ผูกกับเดือนของวันนั้น)
  calShift = (dir) => {
    const anchor = parseIso(this.state.cal_anchor);
    anchor.setMonth(anchor.getMonth() + dir);
    this.setState({ cal_anchor: startOfMonth(isoOf(anchor)), filter_date_start: "", filter_date_end: "" }, this.loadCalendar);
  };
  calToday = () => this.setState({ cal_anchor: startOfMonth(isoOf(new Date())), filter_date_start: "", filter_date_end: "" }, this.loadCalendar);
  onFilterDateChange = ({ start, end }) =>
    this.setState((prev) => {
      const focus = start || end;
      return { filter_date_start: start || "", filter_date_end: end || "", cal_anchor: focus ? startOfMonth(focus) : prev.cal_anchor };
    });
  monthLabel = () => {
    const d = parseIso(this.state.cal_anchor);
    return `${t(MONTH_KEYS[d.getMonth()])} ${yearLabel(d.getFullYear())}`;
  };

  resetFilters = () => {
    this.setState({ filter_search: "", filter_technician: "", filter_owner: "", filter_status: "", filter_date_start: "", filter_date_end: "", job_type: "all" }, this.loadCalendar);
  };

  // ตัวกรองช่าง: รหัส - ชื่อ (รูปแบบเดียวกับฟอร์ม)
  technicianOptions = () => this.state.data_technicians.map((tech) => ({ value: tech.uid, label: technicianCodeName(tech) }));

  // ============================================================
  // ฟอร์ม: เปิด / ปิด / เปลี่ยนค่า
  // ============================================================
  openCreate = () => {
    this.setState({
      modal_show: true,
      modal_edit: false,
      modal_view: false,
      f_plan_uid: "",
      f_plan_type: "",
      f_owner: "",
      f_project_id: "",
      f_project: null,
      f_project_locked: false,
      f_customer: null,
      f_phases: [],
      f_segments: [],
      f_notes: "",
      f_documents: [],
    });
  };

  // เปิดแก้ไขจากการ์ด / แถว / ชื่อโครงการ - โครงการ HOLD แจ้งเตือนแทนการเปิดฟอร์ม
  // occurrence = รอบงานที่คลิกมา (การ์ด/แถว/ช่องวัน) - รอบที่สำเร็จ (เช็คอินแล้ว) แก้ไขไม่ได้ สถานะอื่นแก้ไขได้
  // planUid = แผนที่ต้องการเปิด (มุมมองโครงการมี 1 แถวต่อแผน) ไม่ส่งมา = แผนของรอบงานที่คลิก หรือแผนแรกของโครงการ
  openEdit = async (projectUid, occurrence = null, planUid = null) => {
    const project = this.projectMap()[projectUid];
    if (!project) return;
    const targetPlanUid = planUid || occurrence?.plan_uid || project.plan?.uid || null;
    // รอบงานที่สำเร็จแล้ว = เปิดดูข้อมูลได้แต่แก้ไขไม่ได้ (โหมดดูอย่างเดียว)
    const viewOnly = occurrence?.status === "done" || !can("contract-plan.update");
    if (isHold(project)) {
      alert("", "warning", t("warning"), formatText(t("cp_hold_cannot_edit"), { code: project.project_code || "" }));
      return;
    }
    if (!targetPlanUid) {
      // โครงการยังไม่วางแผน -> เปิดฟอร์มสร้างโดยล็อกโครงการนี้ไว้ (ดึงแถวเต็มพร้อมช่าง/กรอบวันที่)
      if (!can("contract-plan.create")) return;
      loading(true);
      const row = await this.loadProjectRow(projectUid);
      loading(false);
      if (!row) return;
      this.openCreate();
      this.setState({ f_plan_type: row.project_type, f_project_id: projectUid, f_project: row, f_project_locked: true }, () => {
        this.applyProjectDefaults();
        this.SearchFormCustomers("");
      });
      return;
    }
    await this.loadPlanIntoForm(targetPlanUid, { viewOnly });
  };

  // โหลดแผนเต็ม (งวดงาน / แผนย่อย / ไฟล์แนบ) เข้าฟอร์มแก้ไข - ใช้ทั้งเปิดจากปฏิทินและตอนเลือกโครงการที่มีแผนงานดูแลสระอยู่แล้ว
  loadPlanIntoForm = async (planUid, { viewOnly = false } = {}) => {
    loading(true);
    const res = await GET("admin/contract-plans", { uid: planUid });
    loading(false);
    if (!res?.status || !res.data) {
      alert("", "warning", t("warning"), res?.message || t("fail"));
      return false;
    }
    const plan = res.data;
    this.setState({
      modal_show: true,
      modal_edit: true,
      modal_view: viewOnly,
      f_plan_uid: plan.uid,
      f_plan_type: plan.plan_type,
      f_owner: plan.customer?.uid || "",
      f_customer: plan.customer || null,
      f_project_id: plan.project?.uid || "",
      f_project: plan.project || null,
      f_project_locked: false,
      f_segments: this.segmentsFromPlan(plan),
      f_phases: (plan.phases || []).map((ph) => ({
        ...emptyPhase(ph.start_date, ph.end_date),
        uid: ph.uid,
        work_topic_ids: ph.work_topic_ids?.length ? ph.work_topic_ids : ph.work_topic_id ? [ph.work_topic_id] : [],
        work_topic_item_ids: ph.work_topic_item_ids || [],
        technician_ids: ph.technician_ids || [],
        removed_technicians: ph.removed_technicians || [],
        // ถึงวันสิ้นสุดแล้ว (วันนี้ >= วันสิ้นสุดที่บันทึกไว้) และมีการบันทึกการทำงานแล้ว = แก้ไขไม่ได้ - เงื่อนไขเดียวกับ server (หัวข้องานถูกลบจาก master ไปแล้วยังให้แก้ได้)
        locked: !!ph.has_work && !!(ph.work_topic_ids?.length || ph.work_topic_id) && !!ph.end_date && ph.end_date <= this.state.today,
        has_work: !!ph.has_work,
        work_count: ph.work_count || 0,
        last_work_date: ph.last_work_date || null,
        saved_end_date: ph.end_date,
      })),
      f_notes: plan.notes || "",
      f_documents: (plan.attachments || []).map((a) => ({
        uid: a.uid,
        name: a.file_name || a.file_path,
        size: Number(a.file_size) || 0,
        url: a.file_path,
        mime_type: a.mime_type,
        file_type: a.file_type,
        is_uploaded: true,
      })),
    }, () => {
      // แผนเก่าที่ยังไม่มีเจ้าของ -> โหลดลูกค้าของโครงการนี้ให้เลือก
      if (!plan.customer && !viewOnly) this.SearchFormCustomers("");
    });
    return true;
  };

  // แผนย่อยจาก API -> state ของฟอร์ม
  // แผนงานดูแลสระแบบเดิม (ยังไม่มีแผนย่อย) แปลงเป็นแผนที่ 1 ครอบคลุมทั้งกรอบโครงการ จากวันประจำ/เช็คลิสต์ระดับแผน (บันทึกแล้วกลายเป็นแผนย่อยจริง)
  segmentsFromPlan = (plan) => {
    if (plan.plan_type !== "pool_maintenance") return [];
    const segments = plan.segments || [];
    if (segments.length) {
      return segments.map((seg) => emptySegment({
        uid: seg.uid,
        end_date: seg.end_date,
        days: seg.recurring_days || [],
        checklist_id: seg.checklist_template_id || "",
        is_active: seg.is_active !== false,
        inactive_from: seg.inactive_from || null,
        has_checkin: !!seg.has_checkin,
        checkin_count: seg.checkin_count || 0,
        first_checkin_date: seg.first_checkin_date || null,
        last_checkin_date: seg.last_checkin_date || null,
      }));
    }
    if (!plan.project?.range_start_date || !plan.project?.range_finish_date) return [];
    return [emptySegment({
      end_date: plan.project.range_finish_date,
      days: plan.recurring_days || [],
      checklist_id: plan.checklist_template_id || "",
    })];
  };

  closeModal = () => this.setState({ modal_show: false });

  onPlanTypeChange = (value) => {
    this.setState({ f_plan_type: value, f_owner: "", f_project_id: "", f_phases: [], f_segments: [], form_customers: [] }, () => this.SearchFormCustomers(""));
  };

  // เลือกลูกค้า -> โหลดโครงการของลูกค้านั้นตามประเภทที่เลือก
  // ไม่มีโครงการของประเภทนี้ -> ดึงโครงการทุกประเภทของลูกค้ามานับ เพื่อบอกว่าลูกค้ารายนี้มีโครงการประเภทไหน (เรียกเฉพาะตอนผลว่าง)
  onOwnerChange = async (value, item) => {
    this.pinCustomer(item);
    // โครงการล็อกอยู่ (ตัวเลือกลูกค้ากรองตามโครงการแล้ว) - เปลี่ยนเฉพาะเจ้าของแผน ไม่ล้างโครงการ/งวดงาน
    if (this.state.modal_edit || this.state.f_project_locked) return this.setState({ f_owner: value });
    this.setState({ f_owner: value, f_project_id: "", f_phases: [], f_segments: [] });
    if (value && this.state.f_plan_type) {
      loading(true);
      // โหลดใหม่ทุกครั้งที่เลือกลูกค้า - เจ้าของโครงการ (owner) อาจเพิ่งถูกแก้จากเมนูโครงการ
      const list = await this.loadProjectOptions(this.state.f_plan_type, value, true);
      if (!list.length && !this.state.customer_project_types[value]) {
        const res = await GET("admin/contract-plans/projects", { customer_id: value });
        const counts = {};
        this.toArray(res?.data).forEach((p) => {
          const name = typeLabel(p.project_type);
          counts[name] = (counts[name] || 0) + 1;
        });
        this.setState((prev) => ({ customer_project_types: { ...prev.customer_project_types, [value]: counts } }));
      }
      loading(false);
    }
  };

  // ข้อความใต้ช่องโครงการเมื่อลูกค้าที่เลือกไม่มีโครงการของประเภทที่เลือก
  emptyProjectHint = () => {
    const { f_owner, f_plan_type, customer_project_types } = this.state;
    if (!f_owner || !f_plan_type || this.formProjectOptions().length) return "";
    const counts = customer_project_types[f_owner];
    if (!counts) return "";
    const typeName = typeLabel(f_plan_type);
    const others = Object.entries(counts).map(([name, n]) => `${name} ${n} ${t("cp_items_unit")}`).join(", ");
    return others
      ? formatText(t("cp_no_project_for_type"), { type: typeName, others })
      : formatText(t("cp_customer_no_projects"), { type: typeName });
  };

  formProjectOptions = () => this.state.project_options_by_key[this.projectOptionsKey(this.state.f_plan_type, this.state.f_owner)] || [];
  formProject = () =>
    this.formProjectOptions().find((p) => p.uid === this.state.f_project_id)
    || (this.state.f_project && this.state.f_project.uid === this.state.f_project_id ? this.state.f_project : null);

  // โครงการเปิดให้เลือกหลังจากเลือกลูกค้าแล้วเท่านั้น (server ส่งมาเฉพาะของลูกค้านั้นอยู่แล้ว)
  // ลูกค้า + โครงการที่มีแผนแล้ว = สีเทา เลือกไม่ได้ (key ของแผนคือลูกค้า + โครงการ) ต้องเข้าไปแก้ไขแผนเดิมจากปฏิทิน/ตาราง/มุมมองโครงการ
  formProjectSelectOptions = () =>
    this.formProjectOptions()
      .map((p) => ({
        value: p.uid,
        label: `${projectLabel(p)}${isHold(p) ? ` — ${t("cp_hold_suffix")}` : p.plan_count ? ` (${formatText(t("cp_has_plan_suffix"), { count: p.plan_count })})` : ""}`,
        disabled: !isHold(p) && p.plan_count > 0,
        disabled_hint: t("cp_has_plan_edit_hint"),
      }));

  onProjectChange = async (value) => {
    const project = this.formProjectOptions().find((p) => p.uid === value);
    if (project && isHold(project)) {
      alert("", "warning", t("warning"), formatText(t("cp_hold_cannot_create"), { code: project.project_code || "" }));
      return;
    }
    // ลูกค้า + โครงการที่มีแผนแล้วเลือกไม่ได้ (ตัวเลือกเป็นสีเทา) - ต้องแก้ไขจากแผนเดิม
    if (project?.plan_count > 0) return;
    this.setState({ f_project_id: value, f_phases: [], f_segments: [] }, this.applyProjectDefaults);
  };

  // เปลี่ยนโครงการ -> ล้างผู้ปฏิบัติงานในงวดที่ไม่ใช่ช่างของโครงการนี้
  // งานดูแลสระที่ยังไม่มีแผน -> ระบบสร้างแผนที่ 1 ครอบคลุมทั้งกรอบโครงการ (วันเข้าปฏิบัติงาน + เช็คลิสต์ว่าง ให้ผู้ใช้เลือกเอง)
  applyProjectDefaults = () => {
    const project = this.formProject();
    const allowed = new Set((project?.technicians || []).map((tech) => tech.uid));
    this.setState((prev) => {
      const next = { f_phases: prev.f_phases.map((ph) => ({ ...ph, technician_ids: ph.technician_ids.filter((uid) => allowed.has(uid)) })) };
      if (prev.f_plan_type === "pool_maintenance" && !prev.f_segments.length && project?.range_start_date && project?.range_finish_date) {
        next.f_segments = [emptySegment({ end_date: project.range_finish_date })];
      }
      return next;
    });
  };

  // ---------- งวดงาน ----------
  addPhase = () => {
    const project = this.formProject();
    if (!project) {
      alert("", "warning", t("warning"), t("cp_select_project_first"));
      return;
    }
    const blocked = this.phaseAddBlock(project);
    if (blocked) {
      alert("", "warning", t("warning"), blocked);
      return;
    }
    // งวดงานซ้อนทับกันได้ - ทุกงวดเริ่มต้นที่วันเริ่มโครงการ (แก้ได้) วันที่สิ้นสุดให้ผู้ใช้กรอกเอง
    // เพิ่มแล้วเลื่อนไปที่การ์ดงวดใหม่
    const phase = emptyPhase(project.range_start_date || "");
    this.setState((prev) => ({ f_phases: [...prev.f_phases, phase] }), () => this.scrollToPhase(phase.key));
  };
  scrollToPhase = (key) => {
    const el = document.getElementById(`cp-phase-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  // งวดซ้อนทับกันได้ จึงเพิ่มงวดใหม่ได้เสมอเมื่อเลือกโครงการแล้ว - คืนเหตุผลที่เพิ่มไม่ได้ ("" = เพิ่มได้)
  phaseAddBlock = () => "";
  // หุบ/ขยายการ์ดงวดงาน (เฉพาะการแสดงผล ไม่ส่งไป backend)
  togglePhase = (key) => this.updatePhase(key, { collapsed: !this.state.f_phases.find((ph) => ph.key === key)?.collapsed });
  // งวดที่มีการบันทึกการทำงานแล้วลบไม่ได้ (ถึงวันสิ้นสุดแล้วแต่ยังไม่มีการบันทึกการทำงาน = ลบได้)
  removePhase = async (key, index) => {
    const phase = this.state.f_phases.find((ph) => ph.key === key);
    if (!phase) return;
    if (phase.has_work) {
      alert("", "warning", t("warning"), t("cp_phase_cannot_delete_has_work"));
      return;
    }
    const confirmed = await alert("", "info", t("confirm"), formatText(t("cp_confirm_remove_phase"), { index }), t("delete"), t("cancel"));
    if (!confirmed) return;
    this.setState((prev) => ({ f_phases: prev.f_phases.filter((ph) => ph.key !== key) }));
  };
  updatePhase = (key, patch) =>
    this.setState((prev) => ({ f_phases: prev.f_phases.map((ph) => (ph.key === key ? { ...ph, ...patch } : ph)) }));
  toggleItem = (key, itemUid) =>
    this.setState((prev) => ({
      f_phases: prev.f_phases.map((ph) => {
        if (ph.key !== key) return ph;
        const has = ph.work_topic_item_ids.includes(itemUid);
        return { ...ph, work_topic_item_ids: has ? ph.work_topic_item_ids.filter((x) => x !== itemUid) : [...ph.work_topic_item_ids, itemUid] };
      }),
    }));
  toggleWorker = (key, techUid) =>
    this.setState((prev) => ({
      f_phases: prev.f_phases.map((ph) => {
        if (ph.key !== key) return ph;
        const has = ph.technician_ids.includes(techUid);
        return { ...ph, technician_ids: has ? ph.technician_ids.filter((x) => x !== techUid) : [...ph.technician_ids, techUid] };
      }),
    }));

  // หัวข้องาน (เลือกได้หลายหัวข้อ): เฉพาะที่เปิดใช้งานและยังไม่ได้เลือก
  topicOptions = (selectedUids = []) =>
    this.state.data_work_topics
      .filter((topic) => topic.is_active !== false && !selectedUids.includes(topic.uid))
      .map((topic) => ({ value: topic.uid, label: topic.name }));
  topicItems = (topicUid, selectedIds = []) => {
    const topic = this.state.data_work_topics.find((x) => x.uid === topicUid);
    return (topic?.items || []).filter((item) => item.is_active !== false || selectedIds.includes(item.uid));
  };
  addTopic = (key, topicUid) => {
    if (!topicUid) return;
    this.setState((prev) => ({
      f_phases: prev.f_phases.map((ph) => (ph.key === key && !ph.work_topic_ids.includes(topicUid) ? { ...ph, work_topic_ids: [...ph.work_topic_ids, topicUid] } : ph)),
    }));
  };
  // ลบหัวข้อ = เอางานย่อยของหัวข้อนั้นออกด้วย
  removeTopic = (key, topicUid) => {
    const topic = this.state.data_work_topics.find((x) => x.uid === topicUid);
    const itemUids = (topic?.items || []).map((item) => item.uid);
    this.setState((prev) => ({
      f_phases: prev.f_phases.map((ph) => (ph.key === key
        ? { ...ph, work_topic_ids: ph.work_topic_ids.filter((x) => x !== topicUid), work_topic_item_ids: ph.work_topic_item_ids.filter((x) => !itemUids.includes(x)) }
        : ph)),
    }));
  };
  toggleTopicItems = (key, itemUids, selectAll) =>
    this.setState((prev) => ({
      f_phases: prev.f_phases.map((ph) => {
        if (ph.key !== key) return ph;
        const rest = ph.work_topic_item_ids.filter((x) => !itemUids.includes(x));
        return { ...ph, work_topic_item_ids: selectAll ? [...rest, ...itemUids] : rest };
      }),
    }));

  // ---------- งานดูแลสระ: แผนย่อย ----------
  updateSegment = (key, patch) =>
    this.setState((prev) => ({ f_segments: prev.f_segments.map((seg) => (seg.key === key ? { ...seg, ...patch } : seg)) }));
  toggleSegmentDay = (key, dow) =>
    this.setState((prev) => ({
      f_segments: prev.f_segments.map((seg) => {
        if (seg.key !== key) return seg;
        return { ...seg, days: seg.days.includes(dow) ? seg.days.filter((d) => d !== dow) : [...seg.days, dow] };
      }),
    }));
  scrollToSegment = (key) => {
    const el = document.getElementById(`cp-seg-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // แผนใหม่: วันเริ่ม = ถัดจากแผนล่าสุด, วันสิ้นสุด = วันสิ้นสุดโครงการ, วันเข้าปฏิบัติงาน/เช็คลิสต์ว่างให้ผู้ใช้เลือกเอง
  addSegment = () => {
    const project = this.formProject();
    const segments = this.state.f_segments;
    if (!project) {
      alert("", "warning", t("warning"), t("cp_select_project_first"));
      return;
    }
    const last = segments[segments.length - 1];
    if (last && (!last.end_date || last.end_date >= project.range_finish_date)) {
      alert("", "warning", t("warning"), formatText(t("cp_seg_full"), { index: segments.length }));
      this.scrollToSegment(last.key);
      return;
    }
    const seg = emptySegment({ end_date: project.range_finish_date });
    this.setState({ f_segments: [...segments, seg] }, () => this.scrollToSegment(seg.key));
  };

  // ลบได้เฉพาะแผนที่ยังไม่มี Check-in และต้องเหลืออย่างน้อย 1 แผน - แผนถัดไปจะเริ่มต่อจากแผนก่อนหน้าเอง (ไม่เว้นช่วง)
  removeSegment = async (key, index) => {
    const seg = this.state.f_segments.find((x) => x.key === key);
    if (!seg) return;
    if (seg.has_checkin) {
      alert("", "warning", t("warning"), t("cp_seg_cannot_delete_checked_in"));
      return;
    }
    if (this.state.f_segments.length <= 1) {
      alert("", "warning", t("warning"), t("cp_seg_min_one"));
      return;
    }
    const confirmed = await alert("", "info", t("confirm"), formatText(t("cp_confirm_remove_segment"), { index }), t("delete"), t("cancel"));
    if (!confirmed) return;
    this.setState((prev) => ({ f_segments: prev.f_segments.filter((x) => x.key !== key) }));
  };

  // ปิดแผน (Inactive): หยุดสร้างรอบใหม่ตั้งแต่วันที่ปิด (server บันทึกวันที่ปิดตอนกดบันทึก) รอบที่ Check-in แล้วยังเก็บไว้
  toggleSegmentActive = async (key, index) => {
    const seg = this.state.f_segments.find((x) => x.key === key);
    if (!seg) return;
    if (seg.is_active) {
      const confirmed = await alert("", "info", t("confirm"), formatText(t("cp_confirm_close_segment"), { index }), t("cp_seg_close"), t("cancel"));
      if (!confirmed) return;
      this.updateSegment(key, { is_active: false });
      return;
    }
    this.updateSegment(key, { is_active: true, inactive_from: null });
  };

  checklistOptions = (selectedUid = "") =>
    this.state.data_checklists
      .filter((ck) => ck.is_active !== false || ck.uid === selectedUid)
      .map((ck) => ({ value: ck.uid, label: `${ck.name} (${(ck.items || []).length} ${t("cp_items_unit")})` }));

  // ---------- เอกสารแนบ ----------
  addDocuments = (fileList) => {
    const files = Array.from(fileList || []);
    const accepted = [];
    const rejected = [];
    files.forEach((file) => {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const okType = ["pdf", "jpg", "jpeg", "png"].includes(ext);
      if (!okType || file.size > DOC_MAX_SIZE) rejected.push(file.name);
      else accepted.push({ file, name: file.name, size: file.size, url: URL.createObjectURL(file), mime_type: file.type, file_type: ext === "pdf" ? "document" : "image", is_uploaded: false });
    });
    if (rejected.length) alert("", "warning", t("warning"), `${t("cp_doc_rejected")}: ${rejected.join(", ")}`);
    if (accepted.length) this.setState((prev) => ({ f_documents: [...prev.f_documents, ...accepted] }));
  };
  removeDocument = (idx) => this.setState((prev) => ({ f_documents: prev.f_documents.filter((_, i) => i !== idx) }));

  uploadDocuments = async () => {
    const uploaded = [];
    for (const doc of this.state.f_documents) {
      if (doc.is_uploaded) {
        uploaded.push({ uid: doc.uid, file_path: doc.url, file_name: doc.name, file_type: doc.file_type, mime_type: doc.mime_type, file_size: doc.size ?? null });
        continue;
      }
      const res = await UPLOAD(doc.file, { folder: UPLOAD_FOLDER });
      if (!(res?.status === true && res?.data?.file_path)) {
        alert("", "warning", t("warning"), `${t("upload")} "${doc.name}" ${t("fail")}`);
        return null;
      }
      uploaded.push({
        file_path: res.data.file_path,
        file_name: doc.name || res.data.file_name || null,
        file_type: doc.file_type,
        mime_type: res.data.mime_type || doc.mime_type || null,
        file_size: res.data.file_size ?? doc.size ?? null,
      });
    }
    return uploaded;
  };

  // ============================================================
  // บันทึก / ลบ
  // ============================================================
  // โครงการที่ยังไม่มีวันเริ่ม/สิ้นสุดตามแผน ต้องไปลงข้อมูลที่หน้าโครงการก่อน
  projectMissingDates = (project) => project ? [
    !project.range_start_date && t("planned_start_date"),
    !project.range_finish_date && t("planned_finish_date"),
  ].filter(Boolean) : [];

  /**
   * ตรวจแผนย่อยทีละแผน (ตามลำดับ) - คืน { message: "แผนที่ n: เหตุผล", key } ให้ handleSave เลื่อนไปที่การ์ดนั้น
   * วันสิ้นสุด: ไม่ก่อนวันเริ่ม, ไม่เกินวันสิ้นสุดโครงการ, เหลือวันให้แผนถัดไปอย่างน้อย 1 วัน,
   * แผนที่มี Check-in แล้วต้องยังครอบวันที่ Check-in ของตัวเอง
   */
  validateSegments = (project) => {
    const segments = this.state.f_segments;
    if (!segments.length) return { message: t("cp_seg_min_one"), key: null };
    const starts = segmentStarts(project, segments);
    const projectEnd = project.range_finish_date;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const start = starts[i];
      const next = segments[i + 1];
      const fail = (reason) => ({ message: formatText(t("cp_seg_error"), { index: i + 1, reason }), key: seg.key });
      if (!seg.end_date) return fail(t("cp_seg_need_end"));
      if (start && seg.end_date < start) return fail(formatText(t("cp_seg_end_before_start"), { date: fmtDate(start) }));
      if (seg.end_date > projectEnd) return fail(formatText(t("cp_seg_end_after_project"), { date: fmtDate(projectEnd) }));
      if (next && next.end_date && next.end_date <= seg.end_date) return fail(formatText(t("cp_seg_need_next_day"), { next: i + 2 }));
      if (seg.has_checkin && seg.last_checkin_date && seg.end_date < seg.last_checkin_date) {
        return fail(formatText(t("cp_seg_end_before_checkin"), { date: fmtDate(seg.last_checkin_date) }));
      }
      if (seg.has_checkin && seg.first_checkin_date && start > seg.first_checkin_date) {
        return fail(formatText(t("cp_seg_start_after_checkin"), { date: fmtDate(seg.first_checkin_date), prev: i }));
      }
      if (!seg.days.length) return fail(t("cp_need_day"));
      if (!seg.checklist_id) return fail(t("cp_need_checklist"));
    }
    return null;
  };

  validateForm = () => {
    const { f_plan_type, f_phases } = this.state;
    const project = this.formProject();
    if (!f_plan_type) return t("cp_select_plan_type");
    if (!this.state.f_owner) return t("cp_need_customer");
    if (!project) return t("cp_select_project_first");
    const missingDates = this.projectMissingDates(project);
    if (missingDates.length) return formatText(t("cp_project_missing_dates"), { fields: missingDates.join(t("cp_and")) });
    if (f_plan_type === "installation") {
      if (!(project.technicians || []).length) return t("cp_project_no_technician");
      if (!f_phases.length) return t("cp_need_phase");
      for (let i = 0; i < f_phases.length; i++) {
        const ph = f_phases[i];
        const no = i + 1;
        // งวดที่ล็อกแล้ว (ถึงวันสิ้นสุด + มีการบันทึกการทำงาน) แก้ไขไม่ได้ - ไม่ตรวจซ้ำ (server คงค่าที่บันทึกไว้)
        if (ph.locked) continue;
        // งวดที่ตรวจไม่ผ่านต้องไม่ถูกหุบไว้ - ขยายให้ผู้ใช้เห็นช่องที่ต้องแก้
        const fail = (message) => {
          if (ph.collapsed) this.updatePhase(ph.key, { collapsed: false });
          return message;
        };
        if (!ph.work_topic_ids.length) return fail(formatText(t("cp_phase_need_topic"), { index: no }));
        if (!ph.start_date || !ph.end_date) return fail(formatText(t("cp_phase_need_dates"), { index: no }));
        if (ph.start_date > ph.end_date) return fail(formatText(t("cp_phase_date_order"), { index: no }));
        // มีการบันทึกการทำงานแล้ว: วันที่สิ้นสุดที่แก้ใหม่ต้องหลังวันที่บันทึกล่าสุด (ไม่แก้ = ไม่ตรวจ)
        if (ph.has_work && ph.last_work_date && ph.end_date !== ph.saved_end_date && ph.end_date <= ph.last_work_date) {
          return fail(formatText(t("cp_phase_end_after_work"), { index: no, date: fmtDate(ph.last_work_date) }));
        }
        if ((project.range_start_date && ph.start_date < project.range_start_date) || (project.range_finish_date && ph.end_date > project.range_finish_date)) {
          return fail(formatText(t("cp_phase_out_of_range"), { index: no }));
        }
        if (!ph.work_topic_item_ids.length) return fail(formatText(t("cp_phase_need_item"), { index: no }));
      }
    } else {
      const error = this.validateSegments(project);
      if (error) return error;
    }
    return "";
  };

  handleSave = async () => {
    const error = this.validateForm();
    if (error) {
      // ผิดที่แผนย่อยไหน -> เลื่อนไปที่การ์ดนั้นก่อนแจ้งเตือน
      if (error.key) this.scrollToSegment(error.key);
      alert("", "warning", t("warning"), error.message || error);
      return;
    }
    const { f_plan_type, f_plan_uid, f_project_id, f_phases, f_segments, f_notes, modal_edit } = this.state;

    this.setState({ saving: true });
    loading(true);
    const attachments = await this.uploadDocuments();
    if (attachments === null) {
      loading(false);
      this.setState({ saving: false });
      return;
    }

    const payload = {
      customer_id: this.state.f_owner,
      notes: f_notes.trim() || null,
      attachments,
      ...(f_plan_type === "installation"
        ? {
            phases: f_phases.map((ph) => ({
              ...(ph.uid ? { uid: ph.uid } : {}),
              work_topic_ids: ph.work_topic_ids,
              start_date: ph.start_date,
              end_date: ph.end_date,
              work_topic_item_ids: ph.work_topic_item_ids,
              technician_ids: ph.technician_ids,
            })),
          }
        : {
            // วันเริ่มของแต่ละแผนย่อย server คำนวณเอง (วันเริ่มโครงการ / วันถัดจากแผนก่อนหน้า)
            segments: f_segments.map((seg) => ({
              ...(seg.uid ? { uid: seg.uid } : {}),
              end_date: seg.end_date,
              recurring_days: [...seg.days].sort((a, b) => a - b),
              checklist_template_id: seg.checklist_id,
              is_active: seg.is_active,
            })),
          }),
    };

    const res = modal_edit
      ? await PUT(`admin/contract-plans/${f_plan_uid}`, payload)
      : await POST("admin/contract-plans", { company_id: company?.id || "", project_id: f_project_id, ...payload });

    loading(false);
    this.setState({ saving: false });
    if (!res?.status) {
      alert("", "warning", t("warning"), res?.message || t("fail"));
      return;
    }
    alert("", "success", t("success"), modal_edit ? t("cp_saved_edit") : t("cp_saved_create"));
    this.closeModal();
    this.loadProjectOptions(this.state.f_plan_type, this.state.f_owner, true);
    this.loadCalendar();
  };

  handleDelete = async () => {
    const { f_plan_uid, f_plan_type, f_owner } = this.state;
    const confirmed = await alert("", "info", t("confirm"), t("cp_confirm_delete"), t("delete"), t("cancel"));
    if (!confirmed) return;
    loading(true);
    const res = await DELETE(`admin/contract-plans/${f_plan_uid}`);
    loading(false);
    if (!res?.status) {
      alert("", "warning", t("warning"), res?.message || t("fail"));
      return;
    }
    alert("", "success", t("success"), t("cp_deleted"));
    this.closeModal();
    this.loadProjectOptions(f_plan_type, f_owner, true);
    this.loadCalendar();
  };

  // ============================================================
  // RENDER
  // ============================================================
  render() {
    const { view } = this.state;
    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
            <div>
              <h4 className="fw-bold text-dark mb-0 d-flex align-items-center">
                <span className="icon me-2 text-primary">{IC.plan}</span> {t("contract_plan")}
              </h4>
              <div className="text-secondary text-12 mt-1">{t("cp_page_subtitle")}</div>
            </div>
            {can("contract-plan.create") ? (
            <button
              className="btn btn-primary px-3 py-2 rounded-3 fw-bold shadow-sm d-flex align-items-center"
              onClick={this.openCreate}
            >
              <span className="icon me-2">{IC.plus}</span> {t("cp_new_plan")}
            </button>
            ) : null}
          </div>

          {this.renderKpis()}
          {this.renderFilters()}
          {this.renderViewToggle()}

          {view === "calendar" && this.renderCalendarView()}
          {view === "table" && this.renderTableView()}
          {view === "project" && this.renderProjectView()}
        </div>

        {this.renderModal()}
        {this.renderDayModal()}
      </div>
    );
  }

  // การ์ดสรุป = สัปดาห์นี้ (จ.-อา. ที่มีวันนี้) จาก summary.week ของ API ใช้ตัวกรองชุดเดียวกับหน้า ไม่ขึ้นกับเดือนที่กำลังดู
  renderKpis() {
    const week = this.state.summary.week || {};
    const kpis = [
      { icon: IC.calendar, color: "#2563eb", label: t("cp_kpi_week"), value: formatNumber(week.total || 0) },
      { icon: IC.chkCircle, color: "#16a34a", label: t("cp_kpi_done"), value: formatNumber(week.done || 0) },
      // สำเร็จ = รอบที่ check-in จริงแล้ว / รอเข้าปฏิบัติงาน = ทุกรอบที่ยังไม่ check-in (รวมวันที่ผ่านมา) / ยังไม่ CHECK-IN = รอบของวันนี้ที่ยังรออยู่
      { icon: IC.hourglass, color: "#d97706", label: t("cp_kpi_waiting"), value: formatNumber(week.waiting || 0) },
      { icon: IC.warning, color: "#dc2626", label: t("cp_kpi_no_checkin"), value: formatNumber(week.no_checkin || 0) },
    ];
    return (
      <div className="row g-3 mb-3">
        {kpis.map((kpi, i) => (
          <div className="col-12 col-sm-6 col-xl-3" key={i}>
            <div className="bg-white rounded-4 p-3 shadow-sm h-100" style={{ borderLeft: `4px solid ${kpi.color}` }}>
              <div className="text-secondary text-13 mb-2 d-flex align-items-center">
                <span className="icon me-2" style={{ color: kpi.color }}>{kpi.icon}</span> {kpi.label}
              </div>
              <div className="fw-bold text-dark" style={{ fontSize: "30px", lineHeight: 1 }}>{kpi.value}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderFilters() {
    const s = this.state;
    const statusOptions = [
      { value: "active", label: t("cp_status_filter_active") },
      { value: "hold", label: t("cp_status_hold") },
    ];
    return (
      <div className="rounded-4 bg-white p-3 mb-3 shadow-sm border-0" style={{ overflowX: "auto" }}>
        {/* จอแคบ = เลื่อนแถบตัวกรองไปทางขวา ไม่ตัดบรรทัด */}
        <div className="d-flex flex-nowrap gap-2 align-items-center">
          <div className="flex-grow-1" style={{ minWidth: 220 }}>
            {/* ไอคอนค้นหาอยู่ในช่อง (รูปแบบเดียวกับช่องค้นหาเมนูใบงาน / GlobalSearchBox) */}
            <div className="position-relative">
              <span className="icon position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary">{IC.search}</span>
              <input
                type="text"
                className="form-control ps-5"
                style={{ height: "38px" }}
                placeholder={t("cp_search_placeholder")}
                value={s.filter_search}
                onChange={(e) => this.setState({ filter_search: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") this.loadCalendar(); }}
              />
            </div>
          </div>
          <div style={{ width: 170, flexShrink: 0 }}>
            <Select
              className="form-control rounded-3 text-dark"
              options={this.technicianOptions()}
              nowrapOptions
              value={s.filter_technician}
              onChange={(value) => this.setState({ filter_technician: value })}
              placeholder={t("cp_filter_technician")}
            />
          </div>
          <div style={{ width: 190, flexShrink: 0 }}>
            <Select
              className="form-control rounded-3 text-dark"
              options={s.data_customers}
              onSearch={this.SearchCustomers}
              nowrapOptions
              value={s.filter_owner}
              onChange={(value, item) => {
                this.pinCustomer(item);
                this.setState({ filter_owner: value });
              }}
              placeholder={t("cp_filter_owner")}
            />
          </div>
          <div style={{ width: 150, flexShrink: 0 }}>
            <Select
              className="form-control rounded-3 text-dark"
              options={statusOptions}
              value={s.filter_status}
              onChange={(value) => this.setState({ filter_status: value })}
              placeholder={t("cp_filter_status")}
            />
          </div>
          <DateRangeFields
            type={currentLang()}
            className="form-control rounded-3"
            fieldStyle={{ width: 135, flexShrink: 0 }}
            startPlaceholder={t("start_date")}
            endPlaceholder={t("end_date")}
            startValue={s.filter_date_start}
            endValue={s.filter_date_end}
            onChange={this.onFilterDateChange}
          />
          <div className="d-flex gap-2 ms-auto flex-shrink-0">
            <button className="btn btn-secondary btn-sm px-3 fw-bold rounded-3 shadow-sm text-nowrap" style={{ height: "38px" }} onClick={this.loadCalendar}>
              {t("search")}
            </button>
            <button className="btn btn-white border btn-sm px-3 fw-bold rounded-3 shadow-sm text-nowrap" style={{ height: "38px" }} onClick={this.resetFilters}>
              {t("clear_all")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderViewToggle() {
    const { view, job_type } = this.state;
    const views = [
      { key: "calendar", icon: IC.calendar, label: t("cp_view_calendar") },
      { key: "table", icon: IC.table, label: t("cp_view_table") },
      { key: "project", icon: IC.project, label: t("cp_view_project") },
    ];
    const jobOptions = [
      { value: "all", label: `${t("cp_job_type")}: ${t("all")}` },
      { value: "installation", label: `${t("cp_job_type")}: ${typeLabel("installation")}` },
      { value: "pool_maintenance", label: `${t("cp_job_type")}: ${typeLabel("pool_maintenance")}` },
    ];
    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div className="bg-white rounded-3 p-1 shadow-sm d-inline-flex gap-1 border">
          {views.map((v) => {
            const active = view === v.key;
            return (
              <button
                key={v.key}
                className={`btn btn-sm px-3 fw-bold d-flex align-items-center ${active ? "btn-primary text-white" : "btn-white text-dark"}`}
                onClick={() => this.setState({ view: v.key, table_page: 1 })}
              >
                <span className="icon me-2">{v.icon}</span> {v.label}
              </button>
            );
          })}
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div style={{ minWidth: 220 }}>
            <Select
              className="form-control rounded-3 text-dark bg-white"
              options={jobOptions}
              value={job_type}
              onChange={(value) => this.setState({ job_type: value || "all" }, this.loadCalendar)}
              placeholder={t("cp_job_type")}
            />
          </div>
          <div className="d-flex align-items-center gap-3 text-12 fw-bold text-secondary">
            {[
              { c: TYPE_STYLE.pool_maintenance.bar, l: typeLabel("pool_maintenance") },
              { c: TYPE_STYLE.installation.bar, l: typeLabel("installation") },
              { c: TYPE_STYLE.hold.bar, l: t("cp_status_hold") },
            ].map((leg) => (
              <span key={leg.l} className="d-flex align-items-center">
                <span className="d-inline-block rounded me-2" style={{ width: 10, height: 10, backgroundColor: leg.c }}></span>{leg.l}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  renderMonthNav(extra = null) {
    return (
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-white border text-dark" onClick={() => this.calShift(-1)}><span className="icon">{IC.chevL}</span></button>
          <span className="fw-bold text-dark text-16">{this.monthLabel()}</span>
          <button className="btn btn-sm btn-white border text-dark" onClick={() => this.calShift(1)}><span className="icon">{IC.chevR}</span></button>
          <button className="btn btn-sm btn-white border text-dark ms-2" onClick={this.calToday}>{t("today")}</button>
        </div>
        {extra}
      </div>
    );
  }

  // ---------- มุมมองปฏิทิน (เดือน) ----------
  renderCalendarView() {
    const { cal_anchor, today } = this.state;
    const anchor = parseIso(cal_anchor);
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = addDays(isoOf(firstOfMonth), -firstOfMonth.getDay());
    const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const totalRows = Math.ceil((firstOfMonth.getDay() + daysInMonth) / 7);
    const weeks = [];
    for (let w = 0; w < totalRows; w++) weeks.push([0, 1, 2, 3, 4, 5, 6].map((i) => addDays(gridStart, w * 7 + i)));
    const byDate = this.occurrencesByDate();
    const projects = this.projectMap();

    return (
      <div className="bg-white rounded-4 shadow-sm mb-3 p-3">
        {this.renderMonthNav()}
        <div className="table-responsive">
          <table className="table table-bordered mb-0" style={{ tableLayout: "fixed" }}>
            <thead>
              <tr>
                {WEEKDAY_KEYS.map((k) => (
                  <th key={k} className="text-center text-secondary text-13">{t(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi} style={{ height: "120px" }}>
                  {week.map((iso) => {
                    const inMonth = iso.slice(0, 7) === cal_anchor.slice(0, 7);
                    const isToday = iso === today;
                    const occ = inMonth ? byDate[iso] || [] : [];
                    return (
                      <td key={iso} className="p-1 align-top position-relative" style={{ backgroundColor: inMonth ? "#fff" : "#F8FAFC" }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className={`text-13 ${isToday ? "fw-bold" : inMonth ? "text-dark" : "text-secondary"}`}>
                            {isToday ? (
                              <span className="d-inline-flex align-items-center justify-content-center rounded-circle text-white" style={{ width: 22, height: 22, fontSize: 12, backgroundColor: "#111827" }}>{parseIso(iso).getDate()}</span>
                            ) : inMonth ? parseIso(iso).getDate() : ""}
                          </span>
                          {inMonth && can("contract-plan.create") && (
                            <button
                              className="btn p-0 border-0 text-secondary d-inline-flex align-items-center justify-content-center rounded-circle"
                              style={{ width: 20, height: 20, lineHeight: 1, backgroundColor: "#eef0f3" }}
                              title={t("cp_new_plan")}
                              onClick={this.openCreate}
                            >
                              <span className="icon" style={{ fontSize: 10 }}>{IC.plus}</span>
                            </button>
                          )}
                        </div>
                        {occ.slice(0, 3).map((o, i) => {
                          const p = projects[o.project_uid] || {};
                          const hold = o.status === "hold";
                          const st = hold ? TYPE_STYLE.hold : TYPE_STYLE[o.plan_type];
                          const done = o.status === "done";
                          return (
                            <div
                              key={`${o.plan_uid}-${o.phase_uid || "r"}-${i}`}
                              className="rounded-1 px-1 mb-1 text-truncate pointer"
                              style={{ backgroundColor: st.bg, borderLeft: `3px solid ${st.bar}`, fontSize: "11px", color: st.text, fontWeight: 700 }}
                              title={`${p.project_code || ""} • ${typeLabel(o.plan_type)} — ${p.name || ""}${hold ? ` (${t("cp_status_hold")})` : done ? ` (${t("cp_status_done")})` : ""}`}
                              onClick={() => this.openEdit(o.project_uid, o)}
                            >
                              {hold && <span className="badge me-1 text-10" style={{ backgroundColor: "#c2410c" }}>HOLD</span>}
                              {o.plan_type === "pool_maintenance" && <span className="icon me-1" style={{ fontSize: 9 }}>{IC.sync}</span>}
                              {p.project_code || "-"} • {typeLabel(o.plan_type)}
                            </div>
                          );
                        })}
                        {occ.length > 3 && (
                          <div className="text-secondary fw-bold pointer" style={{ fontSize: "10px" }} title={t("cp_day_items_title")} onClick={() => this.setState({ day_modal_date: iso, day_modal_search: "", day_modal_query: "" })}>
                            +{occ.length - 3} {t("cp_items_unit")}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="d-flex flex-wrap gap-3 mt-3 text-12">
          <div className="d-flex align-items-center">
            <span className="icon me-2" style={{ color: TYPE_STYLE.pool_maintenance.bar, fontSize: 11 }}>{IC.sync}</span>
            <span className="text-dark">= {t("cp_legend_recurring")}</span>
          </div>
          {["done", "in_progress", "waiting"].map((k) => (
            <div key={k} className="d-flex align-items-center">
              <span className="d-inline-block rounded me-2" style={{ width: 10, height: 10, backgroundColor: STATUS_STYLE[k].color }}></span>
              <span className="text-dark">{t(STATUS_STYLE[k].labelKey)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---------- รายการงานทั้งหมดของวันเดียว (เปิดจาก "+N รายการ" ในมุมมองปฏิทิน) ----------
  renderDayModal() {
    const { day_modal_date, day_modal_search, day_modal_query, today } = this.state;
    const onSearch = (value, wait = 350) => {
      clearTimeout(this.daySearchTimer);
      this.setState({ day_modal_search: value });
      this.daySearchTimer = setTimeout(() => this.setState({ day_modal_query: value }), wait);
    };
    const close = () => this.setState({ day_modal_date: "" });
    const all = day_modal_date ? this.occurrencesByDate()[day_modal_date] || [] : [];
    const projects = this.projectMap();
    const plansByUid = {};
    this.state.projects.forEach((p) => { if (p.plan) plansByUid[p.plan.uid] = p.plan; });
    const techNames = this.technicianNameMap();
    // ค้นหาจากทุกข้อความที่เกี่ยวกับรอบงาน: โครงการ / ลูกค้า / ช่าง / ประเภทงาน / สถานะ / หมายเหตุ (หลายคำ = ต้องเจอทุกคำ)
    const words = day_modal_query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const rows = all.map((o) => {
      const p = projects[o.project_uid] || {};
      const plan = plansByUid[o.plan_uid];
      const names = (o.technician_uids || []).map((uid) => techNames[uid]).filter(Boolean);
      const owner = plan?.customer || p.owner_name || p.owner_code ? planOwnerLabel(p, plan) : "";
      const st = STATUS_STYLE[o.status] || STATUS_STYLE.waiting;
      const text = [p.project_code, p.name, projectLabel(p), owner, p.owner_name, p.owner_code, plan?.customer?.customer_code, plan?.customer?.full_name_th, plan?.customer?.full_name_en, ...names, typeLabel(o.plan_type), t(st.labelKey), this.occurrenceNote(o)]
        .filter((v) => typeof v === "string" && v).join(" ").toLowerCase();
      return { o, p, names, owner, st, text };
    });
    rows.forEach((r) => { r.match = words.every((w) => r.text.includes(w)); });
    const found = rows.filter((r) => r.match).length;

    return (
      <Modal show={!!day_modal_date} onHide={close} centered scrollable contentClassName="border-0 rounded-4 overflow-hidden shadow-lg">
        <Modal.Header className="bg-white border-bottom px-4 py-3" closeButton>
          <div>
            <h5 className="fw-bold mb-0 text-dark">
              {t("cp_day_items_title")} {day_modal_date ? fmtDate(day_modal_date) : ""}
              {day_modal_date === today && <span className="text-primary text-12 ms-2">({t("today")})</span>}
            </h5>
            <div className="text-secondary text-12 mt-1">{words.length ? `${formatNumber(found)}/${formatNumber(all.length)}` : formatNumber(all.length)} {t("cp_items_unit")}</div>
          </div>
        </Modal.Header>
        <div className="bg-white border-bottom px-3 py-2">
          <div className="position-relative">
            <span className="icon position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary">{IC.search}</span>
            <input
              type="text"
              className="form-control ps-5"
              style={{ height: "38px" }}
              placeholder={t("cp_day_search_placeholder")}
              value={day_modal_search}
              onChange={(e) => onSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onSearch(e.target.value, 0); }}
              autoFocus
            />
          </div>
        </div>
        <Modal.Body className="p-3">
          <div className="text-center text-secondary" style={{ overflow: "hidden", transition: "max-height 0.45s ease, opacity 0.45s ease", maxHeight: found === 0 ? 80 : 0, opacity: found === 0 ? 1 : 0 }}>
            <div className="py-4">{t("no_data_found")}</div>
          </div>
          {/* การ์ดที่ไม่ตรงคำค้นไม่ถูกถอดออก แต่ค่อยๆ จางและหุบลง (และกลับมาแบบเดียวกัน) */}
          {rows.map(({ o, p, names, owner, st, match }, i) => {
            const hold = o.status === "hold";
            const ts = hold ? TYPE_STYLE.hold : TYPE_STYLE[o.plan_type];
            return (
              <div
                key={`${o.plan_uid}-${o.phase_uid || "r"}-${i}`}
                style={{
                  overflow: "hidden",
                  transition: "max-height 0.45s ease, opacity 0.45s ease, transform 0.45s ease, margin-bottom 0.45s ease",
                  maxHeight: match ? 140 : 0,
                  opacity: match ? 1 : 0,
                  transform: match ? "none" : "translateY(-6px)",
                  marginBottom: match ? 8 : 0,
                  pointerEvents: match ? undefined : "none",
                }}
              >
                <div
                  className="rounded-3 p-2 ps-3 pointer"
                  style={{ backgroundColor: ts?.bg, borderLeft: `4px solid ${ts?.bar}` }}
                  title={hold ? t("cp_hold_row_hint") : o.status === "done" ? t("cp_done_row_hint") : t("cp_click_to_edit")}
                  onClick={() => { close(); this.openEdit(o.project_uid, o); }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="fw-bold text-14" style={{ color: ts?.text }}>
                      {o.plan_type === "pool_maintenance" && <span className="icon me-1 text-12">{IC.sync}</span>}
                      {p.project_code || "-"} • {typeLabel(o.plan_type)}
                    </div>
                    <span className="badge rounded-pill px-2 py-1 text-12 fw-bold text-nowrap" style={{ color: st.color, backgroundColor: st.bg }}>
                      <span className="icon me-1">{st.icon}</span>{t(st.labelKey)}
                    </span>
                  </div>
                  {(p.name || owner) && <div className="text-13 text-dark text-truncate">{[p.name, owner].filter(Boolean).join(" — ")}</div>}
                  <div className="text-12 text-secondary text-truncate">{t("technician")}: {names.length ? names.join(", ") : "-"}</div>
                </div>
              </div>
            );
          })}
        </Modal.Body>
      </Modal>
    );
  }

  // ---------- มุมมองตาราง ----------
  renderTableView() {
    const { occurrences, table_status, table_page, today } = this.state;
    const projects = this.projectMap();
    // เจ้าของแผนของแต่ละรอบงาน (โครงการเดียวมีได้หลายแผน/หลายลูกค้า)
    const plansByUid = {};
    this.state.projects.forEach((p) => { if (p.plan) plansByUid[p.plan.uid] = p.plan; });
    const techNames = this.technicianNameMap();
    const rows = table_status === "all" ? occurrences : occurrences.filter((o) => o.status === table_status);
    const totalPages = Math.max(1, Math.ceil(rows.length / TABLE_PAGE_SIZE));
    const page = Math.min(table_page, totalPages);
    const start = (page - 1) * TABLE_PAGE_SIZE;
    const pageRows = rows.slice(start, start + TABLE_PAGE_SIZE);
    // วันที่ / ประเภทงาน / สถานะ แสดงเต็ม (nowrap) ส่วนช่าง ลูกค้า โครงการ หมายเหตุ แสดงสูงสุด 2 บรรทัดแล้วตัดเป็น ...
    const headers = ["#", t("date"), t("technician"), t("cp_customer"), t("cp_project"), t("cp_job_type"), t("note"), t("status")];
    // ตัวกรองสถานะในตาราง: ไม่มี "ยังไม่ CHECK-IN" (ดูจากการ์ดสรุปด้านบนแทน)
    const statusOptions = [
      { value: "all", label: `${t("status")}: ${t("all")}` },
      ...Object.keys(STATUS_STYLE).map((k) => ({ value: k, label: `${t("status")}: ${t(STATUS_STYLE[k].labelKey)}` })),
    ];

    return (
      <div className="bg-white rounded-4 shadow-sm mb-3 p-3">
        {this.renderMonthNav(
          <div className="d-flex align-items-center gap-3">
            <div style={{ minWidth: 220 }}>
              <Select
                className="form-control rounded-3 text-dark bg-white"
                options={statusOptions}
                value={table_status}
                onChange={(value) => this.setState({ table_status: value || "all", table_page: 1 })}
                placeholder={t("status")}
              />
            </div>
          </div>
        )}
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              {/* หัวตารางพื้นเทาอ่อนให้แยกจากแถวข้อมูล - ต้องใส่สีที่ th โดยตรง เพราะ bootstrap กำหนดพื้น th แยก (ใส่ที่ tr จะถูกทับ) */}
              <tr className="fw-bold text-12 text-nowrap">
                {headers.map((h, i) => (
                  <th key={i} className="py-3 px-3 border-0 border-bottom" style={{ backgroundColor: "#f1f3f6", color: "#3a4451", borderColor: "#e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr><td colSpan={headers.length} className="text-center text-secondary py-5">{t("no_data_found")}</td></tr>
              ) : (
                pageRows.map((o, idx) => {
                  const p = projects[o.project_uid] || {};
                  const st = STATUS_STYLE[o.status] || STATUS_STYLE.waiting;
                  const hold = o.status === "hold";
                  const names = (o.technician_uids || []).map((uid) => techNames[uid]).filter(Boolean);
                  return (
                    <tr
                      key={`${o.date}-${o.plan_uid}-${o.phase_uid || "r"}`}
                      className={`border-bottom ${hold ? "" : "pointer"}`}
                      style={hold ? { backgroundColor: "#fafbfc", color: "#94a3b8" } : undefined}
                      title={hold ? t("cp_hold_row_hint") : o.status === "done" ? t("cp_done_row_hint") : t("cp_click_to_edit")}
                      onClick={() => this.openEdit(o.project_uid, o)}
                    >
                      <td className="px-3 text-secondary">{start + idx + 1}</td>
                      <td className="px-3 text-nowrap fw-bold text-dark">
                        {fmtDate(o.date)}
                        {o.date === today && <span className="text-primary text-12 ms-1">({t("today")})</span>}
                      </td>
                      <td className="px-3 text-14"><div style={CELL_ELLIPSIS} title={names.join(", ")}>{names.length ? names.join(", ") : "-"}</div></td>
                      <td className="px-3 text-14"><div style={CELL_ELLIPSIS} title={planOwnerLabel(p, plansByUid[o.plan_uid])}>{plansByUid[o.plan_uid]?.customer || p.owner_name || p.owner_code ? planOwnerLabel(p, plansByUid[o.plan_uid]) : "-"}</div></td>
                      <td className="px-3 text-14"><div style={CELL_ELLIPSIS} title={projectLabel(p)}>{projectLabel(p) || "-"}</div></td>
                      <td className="px-3 text-nowrap text-13">
                        <span className="d-inline-block rounded me-2" style={{ width: 9, height: 9, backgroundColor: TYPE_STYLE[o.plan_type]?.bar }}></span>{typeLabel(o.plan_type)}
                      </td>
                      <td className="px-3 text-13 text-secondary"><div style={CELL_ELLIPSIS} title={this.occurrenceNote(o)}>{this.occurrenceNote(o)}</div></td>
                      <td className="px-3 text-nowrap">
                        <span className="badge rounded-pill px-3 py-1 text-12 fw-bold" style={{ color: st.color, backgroundColor: st.bg }}>
                          <span className="icon me-1">{st.icon}</span>{t(st.labelKey)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-between align-items-center px-1 pt-3 border-top flex-wrap text-secondary text-12 gap-2">
          <div>{t("showing")} {rows.length === 0 ? 0 : formatNumber(start + 1)}-{formatNumber(start + pageRows.length)} {t("of")} {formatNumber(rows.length)}</div>
          <Pagination className="mb-0" count={totalPages} page={page} showFirstButton={false} showLastButton={false} onChange={(_, p) => this.setState({ table_page: p })} />
        </div>
      </div>
    );
  }

  // ---------- มุมมองโครงการ (โครงการ x วันในเดือน) ----------
  renderProjectView() {
    const { cal_anchor, today, occurrences, projects, job_type, summary, show_unplanned } = this.state;
    const anchor = parseIso(cal_anchor);
    const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${cal_anchor.slice(0, 7)}-${pad2(d)}`;
      const dow = parseIso(iso).getDay();
      days.push({ d, iso, dow, weekend: dow === 0 || dow === 6, today: iso === today });
    }

    // แผนที่ plan -> วันที่ -> รอบงาน (1 แถวต่อแผน เพราะโครงการมีได้หลายแผน)
    const cellMap = {};
    occurrences.forEach((o) => { ((cellMap[o.plan_uid] ||= {})[o.date] ||= []).push(o); });
    const planned = projects.filter((p) => p.plan && cellMap[p.plan.uid]);
    const unplanned = projects.filter((p) => !p.plan);
    const plannedProjectCount = new Set(planned.map((p) => p.uid)).size;
    // ลำดับแผนภายในโครงการเดียวกัน (ไว้ติดป้าย "แผนที่ N" เมื่อโครงการมีหลายแผน)
    const planIndex = {};
    const planCount = {};
    planned.forEach((p) => { planCount[p.uid] = (planCount[p.uid] || 0) + 1; planIndex[p.plan.uid] = planCount[p.uid]; });
    const activeToday = occurrences.filter((o) => o.date === today && o.status !== "hold").length;

    const stats = [
      { label: t("cp_stat_planned"), value: formatNumber(plannedProjectCount), bg: "#eef7f3", color: "#16833f", icon: IC.chkCircle },
      { label: t("cp_stat_active_today"), value: formatNumber(activeToday), bg: "#fff6e0", color: "#d97706", icon: IC.hourglass },
      { label: t("cp_stat_unplanned"), value: formatNumber(summary.unplanned_projects ?? unplanned.length), bg: "#fdecec", color: "#dc2626", icon: IC.warning },
    ];

    // ชนิดของแถว = ชนิดของแผนจริง (ตรงกับฟอร์ม) ส่วนโครงการที่ยังไม่มีแผนใช้ project_type
    const rowType = (p) => p.plan?.plan_type || p.project_type;
    const groups = job_type === "all"
      ? PLAN_TYPES.map((type) => ({ type, rows: [...planned.filter((p) => rowType(p) === type), ...(show_unplanned ? unplanned.filter((p) => rowType(p) === type) : [])] }))
      : [{ type: null, rows: [...planned, ...(show_unplanned ? unplanned : [])] }];

    const stickyName = { position: "sticky", left: 0, zIndex: 2, background: "#fff", minWidth: 280, maxWidth: 320 };
    const dayHead = (day) => ({
      position: "sticky", top: 0, zIndex: 3, textAlign: "center", padding: "6px 0", lineHeight: 1.15, fontSize: 11,
      backgroundColor: day.today ? "#111827" : "#f7f9fb", color: day.today ? "#fff" : day.weekend ? "#dc2626" : "#1f2937", minWidth: 34,
    });

    return (
      <>
        <div className="row g-3 mb-3">
          {stats.map((c, i) => (
            <div className="col-12 col-md-4" key={i}>
              <div className="bg-white rounded-4 p-3 shadow-sm d-flex align-items-center gap-3">
                <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 38, height: 38, backgroundColor: c.bg, flexShrink: 0 }}>
                  <span className="icon" style={{ color: c.color }}>{c.icon}</span>
                </div>
                <div>
                  <div className="text-12 text-secondary">{c.label}</div>
                  <div className="fw-bold text-dark text-20">{c.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-4 shadow-sm mb-3 p-3">
          {this.renderMonthNav(
            <button className="btn btn-sm btn-white border text-dark fw-bold" onClick={() => this.setState({ show_unplanned: !show_unplanned }, this.loadCalendar)}>
              {show_unplanned ? t("cp_hide_unplanned") : t("cp_show_unplanned")} ({formatNumber(summary.unplanned_projects ?? unplanned.length)})
            </button>
          )}
          <div className="text-12 text-secondary mb-2">{t("cp_project_view_hint")}</div>
          <div style={{ overflow: "auto", maxHeight: "calc(100vh - 220px)" }}>
            <table className="table table-bordered mb-0" style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: 1400 }}>
              <thead>
                <tr>
                  <th className="text-13 fw-bold text-dark" style={{ ...stickyName, top: 0, zIndex: 4, backgroundColor: "#f7f9fb" }}>{t("cp_project")}</th>
                  {days.map((day) => (
                    <th key={day.iso} style={dayHead(day)}>
                      <div className="fw-bold">{t(WEEKDAY_SHORT_KEYS[day.dow])}</div>
                      <div className="fw-bold" style={{ fontSize: 12 }}>{day.d}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.every((g) => g.rows.length === 0) && (
                  <tr><td colSpan={days.length + 1} className="text-center text-secondary py-4">{t("no_data_found")}</td></tr>
                )}
                {groups.map((g) => (
                  <React.Fragment key={g.type || "all"}>
                    {g.type && g.rows.length > 0 && (
                      <tr>
                        {/* หัวกลุ่มใช้สีประจำประเภทงาน (ฟ้าเข้ม = งานติดตั้ง, ฟ้า = งานดูแลสระ) ให้แยกกลุ่มได้ทันที */}
                        <td className="text-13 fw-bold" style={{ ...stickyName, color: TYPE_STYLE[g.type]?.text }}>
                          <span className="d-inline-block rounded me-2 align-middle" style={{ width: 10, height: 10, backgroundColor: TYPE_STYLE[g.type]?.bar }}></span>{typeLabel(g.type)}
                        </td>
                        {days.map((day) => <td key={day.iso} style={{ backgroundColor: day.weekend ? "#f5f7fa" : "#fff", height: 34 }}></td>)}
                      </tr>
                    )}
                    {g.rows.map((p) => {
                      const hold = isHold(p);
                      const unplannedRow = !p.plan;
                      const rowKey = p.plan ? p.plan.uid : p.uid;
                      const fill = hold ? TYPE_STYLE.hold.cell : TYPE_STYLE[rowType(p)]?.cell;
                      const checkColor = hold ? TYPE_STYLE.hold.bar : TYPE_STYLE[rowType(p)]?.bar;
                      // งานดูแลสระ: วันประจำของแผนย่อยที่ครอบวันนี้ (หรือแผนย่อยแรกในเดือนนี้) - แผนแบบเดิมใช้ค่าระดับแผน
                      const segs = p.plan?.segments || [];
                      const poolDays = segs.length
                        ? (segs.find((x) => x.start_date <= today && x.end_date >= today) || segs[0]).recurring_days || []
                        : p.plan?.recurring_days || [];
                      const dayText = p.plan?.plan_type === "pool_maintenance" && poolDays.length
                        ? ` · ${t("cp_every")} ${DAY_PILLS.filter((x) => poolDays.includes(x.dow)).map((x) => t(x.key)).join(", ")}`
                        : "";
                      return (
                        <tr key={rowKey}>
                          <td className={hold ? "" : "pointer"} style={stickyName} title={hold ? t("cp_hold_row_hint") : t("cp_click_to_edit")} onClick={() => this.openEdit(p.uid, null, p.plan?.uid)}>
                            <div className="d-flex align-items-center gap-2">
                              <div className="fw-bold text-13 text-dark text-truncate">{p.name || "-"}</div>
                              {p.plan && planCount[p.uid] > 1 && <span className="badge rounded-pill text-10 bg-light text-secondary border">{t("cp_plan_no")} {planIndex[p.plan.uid]}</span>}
                              {hold && <span className="badge rounded-pill text-10" style={{ backgroundColor: "#fff4ec", color: "#c2410c" }}>HOLD</span>}
                              {unplannedRow && <span className="badge rounded-pill text-10" style={{ backgroundColor: "#fdecec", color: "#dc2626" }}>{t("cp_unplanned_badge")}</span>}
                            </div>
                            <div className="text-12 text-secondary text-truncate">{[p.project_code, planOwnerLabel(p, p.plan)].filter(Boolean).join(" · ")}{dayText}</div>
                          </td>
                          {days.map((day) => {
                            const cell = (cellMap[rowKey] || {})[day.iso];
                            if (!cell) return <td key={day.iso} style={{ backgroundColor: day.weekend ? "#f5f7fa" : "#fff", height: 48 }}></td>;
                            const done = cell[0].status === "done";
                            return (
                              <td
                                key={day.iso}
                                className={`text-center ${hold ? "" : "pointer"}`}
                                style={{ backgroundColor: fill, height: 48 }}
                                title={`${p.project_code || ""} • ${fmtDate(day.iso)} • ${this.occurrenceNote(cell[0])}${hold ? " (HOLD)" : done ? ` (${t("cp_status_done")})` : ""}`}
                                onClick={() => this.openEdit(p.uid, cell[0], p.plan?.uid)}
                              >
                                <span className="fw-bold" style={{ color: checkColor, fontSize: 15 }}>✓</span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  // ============================================================
  // ฟอร์มสร้าง / แก้ไขแผน
  // ============================================================
  sectionHead = (no, title, right = null) => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h6 className="fw-bold text-dark mb-0 d-flex align-items-center">
        <span className="d-inline-flex align-items-center justify-content-center rounded-2 me-2 text-12 fw-bold" style={{ width: 22, height: 22, backgroundColor: "#dbe8ff", color: "#2f6fed" }}>{no}</span>
        {title}
      </h6>
      {right}
    </div>
  );

  renderModal() {
    const s = this.state;
    const project = this.formProject();
    const missingDates = this.projectMissingDates(project);
    // ส่วนที่ 3 ปิดไว้จนกว่าโครงการจะมีกรอบวันที่ครบ
    const scheduleBlocked = missingDates.length > 0;
    const scheduleBlockedNote = (
      <div className="rounded-3 p-3 text-13 fw-bold" style={{ backgroundColor: "#fdf1f2", color: "#b42318" }}>{t("cp_schedule_need_project_range")}</div>
    );
    const isInstall = s.f_plan_type === "installation";
    const isPool = s.f_plan_type === "pool_maintenance";
    // แผนย่อยล่าสุดถึงวันสิ้นสุดโครงการแล้ว -> ปุ่มเพิ่มแผนงานจางลง (กดแล้วแจ้งให้แก้วันสิ้นสุดของแผนล่าสุดก่อน)
    const lastSegment = s.f_segments[s.f_segments.length - 1];
    const segmentsFull = !!(project && lastSegment && (!lastSegment.end_date || lastSegment.end_date >= project.range_finish_date));
    // งวดงานสุดท้ายยังไม่ระบุวันสิ้นสุด / ถึงวันสิ้นสุดโครงการแล้ว -> ปุ่มเพิ่มงวดงานจางลง (กดแล้วแจ้งเหตุผล)
    const phaseAddBlock = isInstall ? this.phaseAddBlock(project) : "";
    // โหมดแก้ไข: ล็อกประเภทงาน / ลูกค้า / โครงการ (เป็นตัวระบุว่ากำลังแก้แผนของโครงการไหน) - Select รับ disabled แค่เปลี่ยนสี จึงกันคลิกที่นี่
    const locked = s.modal_edit || s.f_project_locked;
    // โหมดดูอย่างเดียว: กันการคลิกทั้งเนื้อหาฟอร์ม (เลื่อนดูได้ เพราะ wheel ตกไปที่ Modal.Body ที่เป็นตัว scroll)
    const lockStyle = locked ? { pointerEvents: "none" } : undefined;
    // กล่องข้อความแนะนำแบบเส้นประ (class .border ของ bootstrap บังคับ solid ด้วย !important จึงกำหนดเองทั้งก้อน)
    const dashedBox = { border: "1px dashed #dde2e8" };

    return (
      <Modal size="lg" show={s.modal_show} onHide={this.closeModal} centered contentClassName="border-0 rounded-4 overflow-hidden shadow-lg bg-light">
        <Modal.Header className="bg-white border-bottom px-4 py-3" closeButton>
          <div className="d-flex align-items-center">
            <div>
              <h5 className="fw-bold mb-0 text-dark d-flex align-items-center">
                <span className="icon me-2 text-primary">{IC.edit}</span>
                {s.modal_view ? t("cp_modal_title_view") : s.modal_edit ? t("cp_modal_title_edit") : t("cp_modal_title_create")}
              </h5>
              <div className="text-secondary text-12 mt-1">{t("cp_modal_sub")}</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4 pt-3" style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {s.modal_view && (
            <div className="rounded-3 px-3 py-2 mb-3 text-12 d-flex align-items-center" style={{ backgroundColor: "#e8f6ee", border: "1px solid #bbe5c8", color: "#166534" }}>
              <span className="icon me-2">{IC.chkCircle}</span>{t("cp_view_only_note")}
            </div>
          )}
          <div style={s.modal_view ? { pointerEvents: "none", opacity: 0.85 } : undefined}>
          <div className="rounded-3 px-3 py-2 mb-3 text-12" style={{ backgroundColor: "#fff8e6", border: "1px solid #fde68a", color: "#92400e" }}>
            {t("cp_draft_note")}
          </div>

          {/* 1. ประเภทงานโครงการ */}
          <div className="bg-white rounded-4 p-4 mb-3 shadow-sm">
            {this.sectionHead(1, t("cp_sec_type"))}
            <div className="row">
              <div className="col-md-12">
                <label className="form-label text-dark fw-bold text-14">{t("cp_plan_type")} <span className="text-danger">*</span></label>
                <div style={lockStyle}>
                  <Select
                    className="form-control rounded-3 text-dark bg-white"
                    options={this.planTypeOptions()}
                    value={s.f_plan_type}
                    onChange={this.onPlanTypeChange}
                    placeholder={t("cp_select_plan_type")}
                    disabled={locked}
                  />
                </div>
                <div className="text-12 text-secondary mt-1">{t("cp_plan_type_hint")}</div>
              </div>
            </div>
            {!s.f_plan_type && (
              <div className="rounded-3 p-3 mt-3 text-center text-12 text-secondary" style={dashedBox}>{t("cp_select_plan_type_note")}</div>
            )}
          </div>

          {s.f_plan_type && (
            <>
              {/* 2. ข้อมูลโครงการ */}
              <div className="bg-white rounded-4 p-4 mb-3 shadow-sm">
                {this.sectionHead(2, t("cp_sec_project"))}
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-dark fw-bold text-14">{t("cp_customer")} <span className="text-danger">*</span></label>
                    {/* ลูกค้าล็อกเมื่อแผนบันทึกเจ้าของไว้แล้ว / โหมดดู - แผนเก่าที่ยังไม่มีลูกค้าเลือกได้จากลูกค้าของโครงการ */}
                    {s.f_customer || s.modal_view ? (
                      <input type="text" className="form-control bg-light" readOnly value={planOwnerLabel(project, { customer: s.f_customer })} />
                    ) : (
                      <Select
                        className="form-control rounded-3 text-dark bg-white"
                        options={this.formCustomerOptions()}
                        onSearch={this.SearchFormCustomers}
                        nowrapOptions
                        value={s.f_owner}
                        onChange={this.onOwnerChange}
                        placeholder={t("cp_select_customer")}
                      />
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-dark fw-bold text-14">{t("cp_project")} <span className="text-danger">*</span> <span className="fw-normal text-secondary">({t("cp_filtered_by_customer")})</span></label>
                    {locked ? (
                      <input type="text" className="form-control bg-light" readOnly value={project ? projectLabel(project) : "-"} />
                    ) : (
                      <div style={!s.f_owner ? { pointerEvents: "none" } : undefined}>
                        <Select
                          className="form-control rounded-3 text-dark bg-white"
                          options={this.formProjectSelectOptions()}
                          nowrapOptions
                          value={s.f_project_id}
                          onChange={this.onProjectChange}
                          placeholder={s.f_owner ? t("cp_select_project") : t("cp_select_customer_first")}
                          disabled={!s.f_owner}
                        />
                        {this.emptyProjectHint() && (
                          <div className="text-12 mt-1" style={{ color: "#c2410c" }}>
                            <span className="icon me-1">{IC.info}</span>{this.emptyProjectHint()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {project ? (
                  <div className="rounded-3 p-3 mt-3 text-13" style={{ backgroundColor: "#f7f9fb", border: "1px solid #eef0f2" }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="icon text-secondary">{IC.calendar}</span>
                      <span>{t("cp_project_range")}: <b className="text-dark">{project.range_start_date || project.range_finish_date ? `${fmtDate(project.range_start_date)} – ${project.range_finish_date ? fmtDate(project.range_finish_date) : t("cp_open_ended")}` : t("cp_no_project_range")}</b></span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="icon text-secondary">{IC.building}</span>
                      <span>{t("project_code")}: <b className="text-dark">{project.project_code || "-"}</b></span>
                    </div>
                    <div className="d-flex align-items-start gap-2 mt-1">
                      <span className="icon text-secondary">{IC.building}</span>
                      <span style={{ whiteSpace: "pre-wrap" }}>{t("description")}: <b className="text-secondary fw-normal">{project.description || "-"}</b></span>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-3 p-3 mt-3 text-center text-12 text-secondary" style={dashedBox}>{t("cp_select_project_note")}</div>
                )}
                {missingDates.length > 0 && (
                  <div className="rounded-3 p-3 mt-2 d-flex gap-2" style={{ backgroundColor: "#fdf1f2", border: "1px solid #f5c2c7" }}>
                    <span className="icon" style={{ color: "#b42318" }}>{IC.warning}</span>
                    <div>
                      <div className="text-13 fw-bold" style={{ color: "#b42318" }}>{t("cp_project_missing_dates_title")}</div>
                      <div className="text-12 mt-1" style={{ color: "#7a271a" }}>{formatText(t("cp_project_missing_dates"), { fields: missingDates.join(t("cp_and")) })}</div>
                    </div>
                  </div>
                )}

                <div className="mt-3">
                  <label className="form-label text-dark fw-bold text-14">{t("cp_workers")}</label>
                  <div className="rounded-3 p-2 px-3 text-13" style={{ backgroundColor: "#f7f9fb", border: "1px solid #eef0f2", color: project && (project.technicians || []).length ? "#3a4451" : "#9aa4b2" }}>
                    {!project ? t("cp_select_project_first") : (project.technicians || []).length ? project.technicians.map(technicianCodeName).join(", ") : t("cp_project_no_technician")}
                  </div>
                  <div className="text-12 text-secondary mt-1">{t("cp_workers_hint")}</div>
                </div>
              </div>

              {/* 3. งานติดตั้ง: วางแผนงาน */}
              {isInstall && (
                <div className="bg-white rounded-4 p-4 mb-3 shadow-sm">
                  {this.sectionHead(3, t("cp_sec_phases"), (
                    <button
                      className="btn btn-sm fw-bold rounded-3"
                      style={{ backgroundColor: "#eaf1ff", color: "#2f6fed", opacity: phaseAddBlock ? 0.55 : 1 }}
                      onClick={this.addPhase}
                      disabled={!project || scheduleBlocked}
                      title={phaseAddBlock}
                    >
                      <span className="icon me-1">{IC.plus}</span>{t("cp_add_phase")}
                    </button>
                  ))}
                  {scheduleBlocked ? scheduleBlockedNote : (<>
                  <div className="text-12 text-secondary mb-3">{t("cp_phases_hint")}</div>
                  {s.f_phases.length === 0 ? (
                    <div className="rounded-3 p-3 text-center text-12 text-secondary" style={dashedBox}>{t("cp_no_phase")}</div>
                  ) : (
                    s.f_phases.map((ph, i) => this.renderPhase(ph, i, project))
                  )}
                  </>)}
                </div>
              )}

              {/* 3. งานดูแลสระ: แผนย่อย (แผนที่ 1, 2, ...) */}
              {isPool && (
                <div className="bg-white rounded-4 p-4 mb-3 shadow-sm">
                  {this.sectionHead(3, t("cp_sec_schedule"), (
                    <button
                      className="btn btn-sm fw-bold rounded-3"
                      style={{ backgroundColor: "#eaf1ff", color: "#2f6fed", opacity: segmentsFull ? 0.55 : 1 }}
                      onClick={this.addSegment}
                      disabled={!project || scheduleBlocked}
                      title={segmentsFull ? formatText(t("cp_seg_full"), { index: s.f_segments.length }) : ""}
                    >
                      <span className="icon me-1">{IC.plus}</span>{t("cp_add_sub_plan")}
                    </button>
                  ))}
                  {scheduleBlocked ? scheduleBlockedNote : this.renderSegments(project)}
                </div>
              )}

              {/* 4. เพิ่มเติม - ปิดพร้อมส่วนที่ 3 (จางลงและกันคลิก/ลากไฟล์) */}
              <div className="bg-white rounded-4 p-4 mb-3 shadow-sm" style={scheduleBlocked ? { opacity: 0.45, pointerEvents: "none" } : undefined} aria-disabled={scheduleBlocked}>
                {this.sectionHead(4, t("cp_sec_more"))}
                <label className="form-label text-dark fw-bold text-14">{t("documents")} <span className="fw-normal text-secondary">({t("cp_optional")})</span></label>
                <div
                  className="rounded-3 p-4 mb-2 text-center pointer"
                  style={{ border: "1.5px dashed #2563EB", backgroundColor: "#EFF4FF" }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); this.addDocuments(e.dataTransfer?.files); }}
                  onClick={() => document.getElementById("cp-doc-upload").click()}
                >
                  <input type="file" id="cp-doc-upload" multiple accept={DOC_ACCEPT} hidden onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; this.addDocuments(files); }} />
                  <span className="icon text-30 text-primary mb-2 d-block">{IC.file}</span>
                  <div className="fw-bold text-dark text-14">{t("click_or_drag_document_here")}</div>
                  <div className="text-12 text-secondary mt-1">{t("cp_doc_hint")}</div>
                </div>
                {s.f_documents.map((doc, i) => (
                  <div key={i} className="d-flex align-items-center justify-content-between border rounded-3 p-2 mb-2 bg-light">
                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                      <span className="icon text-16 text-primary">{IC.file}</span>
                      <div className="overflow-hidden">
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-12 fw-bold text-dark text-truncate d-block text-decoration-none" title={doc.name}>{doc.name}</a>
                        <div className="text-10 text-secondary">{formatFileSize(doc.size || 0)}</div>
                      </div>
                    </div>
                    <button className="btn btn-sm text-danger fw-bold" onClick={() => this.removeDocument(i)} title={t("delete")}><span className="icon">{IC.times}</span></button>
                  </div>
                ))}

                <label className="form-label text-dark fw-bold text-14 mt-2">{t("cp_notes")}</label>
                <textarea className="form-control" rows="3" disabled={scheduleBlocked} value={s.f_notes} placeholder={isPool ? t("cp_notes_placeholder_pool") : t("cp_notes_placeholder")} onChange={(e) => this.setState({ f_notes: e.target.value })}></textarea>
              </div>
            </>
          )}
          </div>
        </Modal.Body>

        {/* ปุ่มลบแผนตัดออกก่อน (ยังไม่ใช้) - API DELETE และ handleDelete ยังอยู่ เผื่อเปิดใช้ภายหลัง */}
        <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-end gap-2">
          <div className="d-flex gap-2">
            {s.modal_view ? (
              <button className="btn btn-white border text-dark fw-bold rounded-3 px-4" onClick={this.closeModal}>{t("close")}</button>
            ) : (
              <>
                <button className="btn btn-white border text-dark fw-bold rounded-3 px-4" onClick={this.closeModal} disabled={s.saving}>{t("cancel")}</button>
                <button className="btn btn-primary fw-bold rounded-3 px-4" onClick={this.handleSave} disabled={s.saving || missingDates.length > 0}>
                  <span className="icon me-2">{IC.save}</span>{s.modal_edit ? t("cp_save_edit") : t("cp_save_plan")}
                </button>
              </>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  renderPhase(ph, index, project) {
    const topicGroups = ph.work_topic_ids
      .map((uid) => ({ uid, name: (this.state.data_work_topics.find((x) => x.uid === uid) || {}).name || "-", items: this.topicItems(uid, ph.work_topic_item_ids) }));
    // เห็นเฉพาะช่างในแผนกของ Admin (data_technicians ถูกกรองจาก backend แล้ว)
    const scopedTechUids = new Set(this.state.data_technicians.map((tech) => tech.uid));
    const workers = (project?.technicians || []).filter((tech) => scopedTechUids.has(tech.uid));
    const outOfRange = project && ph.start_date && ph.end_date && (
      ph.start_date > ph.end_date
      || (project.range_start_date && ph.start_date < project.range_start_date)
      || (project.range_finish_date && ph.end_date > project.range_finish_date)
    );
    const q = (ph.worker_search || "").trim().toLowerCase();
    const candidates = workers.filter((tech) => !q || technicianCodeName(tech).toLowerCase().includes(q));
    const selectedNames = workers.filter((tech) => ph.technician_ids.includes(tech.uid)).map(technicianCodeName);
    const locked = ph.locked; // ถึงวันสิ้นสุดแล้วและมีการบันทึกการทำงานแล้ว: แก้ไขไม่ได้ - ปิดเฉพาะตัว input (หัวข้อ/ข้อความไม่จางตาม) และกันคลิกทั้งส่วน
    const lockedField = locked ? { pointerEvents: "none" } : undefined;
    // ถึงวันสิ้นสุดแล้วแต่ยังไม่มีข้อมูลบันทึก = ยังแก้ไขได้ทุกอย่าง - ติดป้ายบอกเหตุผลไว้หลังชื่องวด
    const endedNoWork = !!ph.uid && !ph.has_work && !!ph.saved_end_date && ph.saved_end_date <= this.state.today;
    // งวดซ้อนทับกันได้: วันที่เริ่ม/สิ้นสุดจำกัดแค่กรอบวันที่ของโครงการ
    const startMin = project?.range_start_date || "";
    // วันที่สิ้นสุด: ไม่ก่อนวันที่เริ่ม / หลังวันที่บันทึกการทำงานล่าสุด และไม่เกินวันสิ้นสุดโครงการ
    const workEndMin = ph.has_work && ph.last_work_date ? addDays(ph.last_work_date, 1) : "";
    const endMin = maxIso(ph.start_date || startMin, workEndMin);
    const endMax = project?.range_finish_date || "";
    const collapsed = !!ph.collapsed;
    const topicName = topicGroups.map((g) => g.name).join(", ");

    return (
      <div key={ph.key} id={`cp-phase-${ph.key}`} className="rounded-3 p-3 mb-3" style={{ border: "1px solid #e5e7eb", backgroundColor: "#fafbfc" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-sm p-0 d-inline-flex align-items-center justify-content-center text-secondary"
              style={{ width: 22, height: 22, border: "1px solid #dde2e8", borderRadius: 4, backgroundColor: "#fff" }}
              title={collapsed ? t("cp_phase_expand") : t("cp_phase_collapse")}
              onClick={() => this.togglePhase(ph.key)}
            >
              <span className="icon text-11">{collapsed ? IC.chevR : IC.chevD}</span>
            </button>
            <span className="fw-bold text-13 text-dark">{t("cp_phase")} {index + 1}</span>
            {collapsed && (
              <>
                <span className="badge rounded-pill text-11" style={{ backgroundColor: "#eaf1ff", color: "#2f6fed" }}>{topicName || t("cp_phase_no_topic")}</span>
                <span className="text-12 text-secondary">
                  <span className="icon me-1">{IC.calendar}</span>{ph.start_date ? fmtDate(ph.start_date) : "-"} - {ph.end_date ? fmtDate(ph.end_date) : "-"}
                </span>
                <span className="text-12 text-secondary"><span className="icon me-1">{IC.check}</span>{formatText(t("cp_phase_summary_tasks"), { count: ph.work_topic_item_ids.length })}</span>
                <span className="text-12 text-secondary" title={selectedNames.join(", ")}>
                  <span className="icon me-1">{IC.users}</span>{selectedNames.length ? selectedNames.join(", ") : t("cp_phase_all_workers")}
                </span>
              </>
            )}
            {locked && (
              <span className="badge rounded-pill text-11" style={{ backgroundColor: "#eef0f2", color: "#475569" }}>
                <span className="icon me-1">{IC.chkCircle}</span>{t("cp_phase_ended_badge")}
              </span>
            )}
            {endedNoWork && (
              <span className="badge rounded-pill text-11" style={{ backgroundColor: "#fdf3e4", color: "#b45309" }}>
                <span className="icon me-1">{IC.info}</span>{t("cp_phase_ended_no_work_badge")}
              </span>
            )}
            {ph.has_work && (
              <span className="badge rounded-pill text-11" style={{ backgroundColor: "#e8f6ee", color: "#166534" }}>
                <span className="icon me-1">{IC.clipboard}</span>{formatText(t("cp_phase_has_work_badge"), { count: formatNumber(ph.work_count) })}
              </span>
            )}
          </div>
          <button
            className="btn btn-sm text-danger rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
            style={{ width: 28, height: 28, backgroundColor: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(16,24,40,0.06)", opacity: ph.has_work ? 0.4 : 1 }}
            title={ph.has_work ? t("cp_phase_cannot_delete_has_work") : t("delete")}
            onClick={() => this.removePhase(ph.key, index + 1)}
          >
            <span className="icon">{IC.times}</span>
          </button>
        </div>
        {!collapsed && (locked || ph.has_work) && (
          <div className="rounded-3 px-3 py-2 mb-3 text-12" style={{ backgroundColor: "#f1f3f6", border: "1px solid #e5e7eb", color: "#475569" }}>
            {locked && <div><span className="icon me-1">{IC.info}</span>{t("cp_phase_ended_note")}</div>}
            {ph.has_work && !locked && (
              <div>
                <span className="icon me-1">{IC.info}</span>
                {ph.last_work_date ? formatText(t("cp_phase_has_work_note"), { date: fmtDate(ph.last_work_date), min: fmtDate(workEndMin) }) : t("cp_phase_cannot_delete_has_work")}
              </div>
            )}
          </div>
        )}
        {!collapsed && (
        <div style={lockedField}>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label text-13 text-secondary mb-1">{t("cp_work_topic")} <span className="text-danger">*</span></label>
            <Select
              className="form-control rounded-3 text-dark bg-white"
              options={this.topicOptions(ph.work_topic_ids)}
              nowrapOptions
              value=""
              onChange={(value) => this.addTopic(ph.key, value)}
              placeholder={t("cp_select_work_topic")}
              disabled={locked}
            />
            {topicGroups.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mt-2">
                {topicGroups.map((g) => (
                  <span key={g.uid} className="badge rounded-pill text-12 d-inline-flex align-items-center gap-1 fw-normal" style={{ backgroundColor: "#eaf1ff", color: "#1e4fc4", border: "1px solid #c9dafb" }}>
                    {g.name}
                    {!locked && (
                      <span className="pointer" role="button" title={t("delete")} onClick={() => this.removeTopic(ph.key, g.uid)}>
                        <span className="icon text-11">{IC.times}</span>
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label text-13 text-secondary mb-1">{t("cp_start_date")} <span className="text-danger">*</span></label>
            <DatePicker type="th" className="form-control rounded-3" value={ph.start_date} min={startMin} max={endMax} onChange={(val) => this.updatePhase(ph.key, { start_date: val })} disabled={locked || ph.has_work} />
          </div>
          <div className="col-md-4">
            <label className="form-label text-13 text-secondary mb-1">{t("cp_end_date")} <span className="text-danger">*</span></label>
            <DatePicker type="th" className="form-control rounded-3" value={ph.end_date} min={endMin} max={endMax} onChange={(val) => this.updatePhase(ph.key, { end_date: val })} disabled={locked} />
          </div>
        </div>
        {outOfRange && (
          <div className="text-12 text-danger fw-bold mt-2">
            <span className="icon me-1">{IC.warning}</span>{formatText(t("cp_phase_range_warn"), { start: fmtDate(project.range_start_date), end: project.range_finish_date ? fmtDate(project.range_finish_date) : t("cp_open_ended") })}
          </div>
        )}

        <div className="mt-3">
          <label className="form-label text-13 fw-bold text-dark mb-2">{t("cp_tasks")} <span className="fw-normal text-secondary">— {t("cp_tasks_hint")}</span></label>
          {!topicGroups.length ? (
            <div className="text-12 text-secondary">{t("cp_select_topic_first")}</div>
          ) : (
            topicGroups.map((g) => {
              const uids = g.items.map((item) => item.uid);
              const count = uids.filter((x) => ph.work_topic_item_ids.includes(x)).length;
              const allSel = uids.length > 0 && count === uids.length;
              return (
                <div key={g.uid} className="mb-3">
                  <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
                    <span className="fw-bold text-13 text-dark">▸ {g.name}</span>
                    {uids.length > 0 && (
                      <>
                        <label className="d-flex align-items-center gap-1 text-12 mb-0 pointer">
                          <input type="checkbox" checked={allSel} disabled={locked} onChange={() => this.toggleTopicItems(ph.key, uids, !allSel)} /> {t("cp_tasks_select_all")}
                        </label>
                        <span className="text-12 text-secondary">{formatText(t("cp_tasks_selected_count"), { selected: count, total: uids.length })}</span>
                      </>
                    )}
                  </div>
                  {g.items.length === 0 ? (
                    <div className="text-12 text-secondary">{t("cp_topic_no_items")}</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {g.items.map((item) => {
                        const sel = ph.work_topic_item_ids.includes(item.uid);
                        return (
                          <button
                            key={item.uid}
                            type="button"
                            className="btn btn-sm rounded-1 text-12 d-flex align-items-center gap-2"
                            style={{ border: `1px solid ${sel ? "#2f6fed" : "#dde2e8"}`, backgroundColor: sel ? "#eaf1ff" : "#fff", color: sel ? "#1e4fc4" : "#3a4451", fontWeight: sel ? 600 : 400, borderRadius: 8 }}
                            onClick={() => this.toggleItem(ph.key, item.uid)}
                            disabled={locked}
                          >
                            {/* กล่องติ๊ก: ต้องมี class "icon" ถึงจะวาดตัวอักษร FontAwesome ของเครื่องหมายถูกได้ */}
                            <span
                              className="icon d-inline-flex align-items-center justify-content-center"
                              style={{ width: 13, height: 13, border: `1.5px solid ${sel ? "#2f6fed" : "#c6ccd3"}`, borderRadius: 2, backgroundColor: sel ? "#2f6fed" : "#fff", color: "#fff", fontSize: 10, fontWeight: 900, lineHeight: 1, flexShrink: 0 }}
                            >
                              {sel ? IC.check : ""}
                            </span>
                            {item.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="mt-3 position-relative">
          <label className="form-label text-13 fw-bold text-dark mb-2">{t("cp_phase_workers")} <span className="fw-normal text-secondary">— {t("cp_phase_workers_hint")}</span></label>
          <input
            type="text"
            readOnly
            className={`form-control rounded-3 text-13 text-truncate ${workers.length && !locked ? "pointer bg-white" : "bg-light"}`}
            value={selectedNames.join(", ")}
            placeholder={t("cp_select_workers")}
            title={selectedNames.join(", ")}
            disabled={locked}
            onClick={() => workers.length && this.updatePhase(ph.key, { worker_open: !ph.worker_open, worker_search: "" })}
          />
          {ph.worker_open && (
            <div className="bg-white border rounded-3 shadow p-2 mt-1" style={{ position: "absolute", left: 0, right: 0, zIndex: 20, maxHeight: 240, overflowY: "auto" }}>
              <input type="text" className="form-control form-control-sm mb-2" placeholder={t("cp_search_worker")} value={ph.worker_search} onChange={(e) => this.updatePhase(ph.key, { worker_search: e.target.value })} autoFocus />
              {candidates.length === 0 ? (
                <div className="text-12 text-secondary px-1">{t("no_data_found")}</div>
              ) : (
                candidates.map((tech) => (
                  <label key={tech.uid} className="d-flex align-items-center gap-2 px-1 py-1 text-13 pointer mb-0 rounded-2">
                    <input type="checkbox" checked={ph.technician_ids.includes(tech.uid)} onChange={() => this.toggleWorker(ph.key, tech.uid)} /> {technicianCodeName(tech)}
                  </label>
                ))
              )}
              <div className="text-end mt-1"><span className="text-primary text-12 fw-bold pointer" onClick={() => this.updatePhase(ph.key, { worker_open: false })}>{t("cp_done")}</span></div>
            </div>
          )}
          <div className="text-12 text-secondary mt-1">{workers.length ? t("cp_phase_workers_note") : t("cp_project_no_technician")}</div>
          {(ph.removed_technicians || []).map((tech) => {
            const d = String(tech.removed_at || "").slice(0, 10).split("-");
            const date = d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : "";
            return (
              <div key={tech.uid} className="text-12 mt-1 rounded-2 px-2 py-1" style={{ backgroundColor: "#fff4e5", border: "1px solid #ffd8a8", color: "#b25e09" }}>
                ⚠ {technicianCodeName(tech)} {t("cp_tech_removed_from_project")} {date}
              </div>
            );
          })}
        </div>
        </div>
        )}
      </div>
    );
  }

  // ---------- งานดูแลสระ: แผนย่อย ----------
  renderSegments(project) {
    const segments = this.state.f_segments;
    if (!project) return <div className="rounded-3 p-3 text-center text-12 text-secondary" style={{ border: "1px dashed #dde2e8" }}>{t("cp_select_project_first")}</div>;
    const starts = segmentStarts(project, segments);
    const projectEnd = project.range_finish_date;
    const last = segments[segments.length - 1];
    // ช่วงท้ายโครงการที่ยังไม่มีแผน (บันทึกได้ แต่ต้องแจ้งให้เห็น)
    const tailStart = last?.end_date && last.end_date < projectEnd ? addDays(last.end_date, 1) : "";
    const infoBox = { backgroundColor: "#eef4ff", border: "1px solid #d6e4ff", color: "#1e3a8a" };

    return (
      <>
        <div className="rounded-3 px-3 py-2 mb-2 text-13 d-flex align-items-center gap-2" style={infoBox}>
          <span className="icon">{IC.calendar}</span>
          <span>
            {t("cp_seg_project_range")}: <b>{fmtDate(project.range_start_date)} – {fmtDate(projectEnd)}</b>
            {" · "}{project.project_code || "-"}{" · "}{formatText(t("cp_seg_plan_count"), { count: segments.length })}
          </span>
        </div>
        {tailStart && (
          <div className="rounded-3 px-3 py-2 mb-2 text-12" style={{ ...infoBox, color: "#1e4fc4" }}>
            {formatText(t("cp_seg_tail"), { start: fmtDate(tailStart), end: fmtDate(projectEnd), days: daysBetween(tailStart, projectEnd) + 1 })}
          </div>
        )}
        {segments.map((seg, i) => this.renderSegment(seg, i, starts, project))}
      </>
    );
  }

  renderSegment(seg, index, starts, project) {
    const segments = this.state.f_segments;
    const no = index + 1;
    const start = starts[index];
    const next = segments[index + 1];
    const projectEnd = project.range_finish_date;
    const locked = seg.has_checkin; // มี Check-in แล้ว: แก้ได้เฉพาะวันสิ้นสุด ลบไม่ได้
    const inactiveDate = seg.is_active ? null : seg.inactive_from || this.state.today;
    const validRange = start && seg.end_date && seg.end_date >= start;
    const rounds = validRange ? countRounds(start, seg.end_date, seg.days, inactiveDate) : { total: 0, months: [] };
    const dayCount = validRange ? daysBetween(start, seg.end_date) + 1 : 0;

    // ช่วงวันสิ้นสุดที่เลือกได้: ไม่ก่อนวันเริ่ม (และวัน Check-in ล่าสุดของแผนนี้)
    // ไม่เกินวันสิ้นสุดโครงการ / ต้องเหลือวันให้แผนถัดไปอย่างน้อย 1 วัน / ต้องจบก่อนวันที่แผนถัดไปมี Check-in
    const endMin = maxIso(start, locked ? seg.last_checkin_date : "");
    const endMax = minIso(
      projectEnd,
      next?.end_date ? addDays(next.end_date, -1) : "",
      next?.has_checkin && next.first_checkin_date ? addDays(next.first_checkin_date, -1) : "",
    );
    const endHint = next
      ? formatText(t("cp_seg_end_hint_next"), { date: fmtDate(endMax), index: no + 1 })
      : formatText(t("cp_seg_end_hint_last"), { date: fmtDate(endMax) });

    const pillColor = seg.is_active ? "#2f6fed" : "#94a3b8";
    const circleBtn = { width: 28, height: 28, backgroundColor: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(16,24,40,0.06)" };
    const lockedField = locked ? { pointerEvents: "none", opacity: 0.6 } : undefined;

    return (
      <div key={seg.key} id={`cp-seg-${seg.key}`} className="rounded-4 mb-3 overflow-hidden" style={{ border: "1px solid #e5e7eb" }}>
        {/* หัวการ์ด: แผนที่ n · ช่วงวันที่ · N วัน · N รอบ */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-2 bg-white" style={{ borderBottom: "1px solid #eef0f2" }}>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="badge rounded-pill text-12 fw-bold px-3 py-1" style={{ backgroundColor: pillColor, color: "#fff" }}>{t("cp_plan_no")} {no}</span>
            <span className="text-14">
              <b className="text-dark">{fmtDate(start)} – {fmtDate(seg.end_date)}</b>
              <span className="text-secondary"> · {formatNumber(dayCount)} {t("cp_seg_days_unit")} · {formatNumber(rounds.total)} {t("cp_seg_rounds_unit")}</span>
            </span>
            {locked && (
              <span className="badge rounded-pill text-11" style={{ backgroundColor: "#e8f6ee", color: "#166534" }}>
                <span className="icon me-1">{IC.chkCircle}</span>{formatText(t("cp_seg_checked_in_badge"), { count: formatNumber(seg.checkin_count) })}
              </span>
            )}
            {!seg.is_active && (
              <span className="badge rounded-pill text-11" style={{ backgroundColor: "#eef0f2", color: "#475569" }}>
                <span className="icon me-1">{IC.pause}</span>{formatText(t("cp_seg_inactive_badge"), { date: fmtDate(inactiveDate) })}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <button type="button" className="btn btn-sm btn-white border text-12 fw-bold rounded-3" onClick={() => this.toggleSegmentActive(seg.key, no)}>
              <span className="icon me-1">{seg.is_active ? IC.pause : IC.sync}</span>{seg.is_active ? t("cp_seg_close") : t("cp_seg_reopen")}
            </button>
            <button
              type="button"
              className="btn btn-sm text-danger rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
              style={{ ...circleBtn, opacity: locked || segments.length <= 1 ? 0.4 : 1 }}
              title={locked ? t("cp_seg_cannot_delete_checked_in") : segments.length <= 1 ? t("cp_seg_min_one") : t("delete")}
              onClick={() => this.removeSegment(seg.key, no)}
            >
              <span className="icon">{IC.times}</span>
            </button>
          </div>
        </div>

        <div className="p-3" style={{ backgroundColor: "#fafbfc" }}>
          {locked && (
            <div className="rounded-3 px-3 py-2 mb-3 text-12" style={{ backgroundColor: "#e8f6ee", border: "1px solid #bbe5c8", color: "#166534" }}>
              <span className="icon me-1">{IC.info}</span>{t("cp_seg_checked_in_note")}
            </div>
          )}

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label text-13 fw-bold text-dark mb-1">{t("cp_seg_start")}</label>
              <div className="form-control rounded-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: "#f1f3f5" }}>
                <span className="fw-bold text-dark">{fmtDate(start)}</span>
                <span className="text-11 text-secondary">{t("cp_seg_system_set")}</span>
              </div>
              <div className="text-12 text-secondary mt-1">
                {index === 0 ? t("cp_seg_start_hint_first") : formatText(t("cp_seg_start_hint_next"), { index })}
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label text-13 fw-bold text-dark mb-1">{t("cp_seg_end")} <span className="text-danger">*</span></label>
              <DatePicker type="th" className="form-control rounded-3" value={seg.end_date} min={endMin} max={endMax} onChange={(val) => this.updateSegment(seg.key, { end_date: val })} />
              <div className="text-12 text-secondary mt-1">{endHint}</div>
              {locked && seg.last_checkin_date && (
                <div className="text-12 text-secondary">{formatText(t("cp_seg_end_hint_checkin"), { date: fmtDate(seg.last_checkin_date) })}</div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 mb-2">
            <label className="form-label text-13 fw-bold text-dark mb-0">{t("cp_recurring_days")} <span className="text-danger">*</span></label>
            {!locked && (
              <div className="d-flex align-items-center gap-3 text-12">
                <label className="d-flex align-items-center gap-1 pointer mb-0">
                  <input type="checkbox" checked={seg.days.length === DAY_PILLS.length} onChange={(e) => this.updateSegment(seg.key, { days: e.target.checked ? DAY_PILLS.map((p) => p.dow) : [] })} /> {t("cp_select_all_days")}
                </label>
                <span className="text-primary fw-bold pointer" onClick={() => this.updateSegment(seg.key, { days: [] })}>{t("cp_clear")}</span>
              </div>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2" style={lockedField}>
            {DAY_PILLS.map((pill) => {
              const sel = seg.days.includes(pill.dow);
              return (
                <button
                  key={pill.dow}
                  type="button"
                  className="btn btn-sm fw-bold rounded-3"
                  style={{ width: 48, border: `1.5px solid ${sel ? "#2f6fed" : "#e3e7eb"}`, backgroundColor: sel ? "#eaf1ff" : "#fff", color: sel ? "#2f6fed" : "#6b7280" }}
                  onClick={() => this.toggleSegmentDay(seg.key, pill.dow)}
                  disabled={locked}
                >
                  {t(pill.key)}
                </button>
              );
            })}
          </div>

          <label className="form-label text-13 fw-bold text-dark mt-3 mb-1">{t("cp_checklist")} <span className="text-danger">*</span></label>
          <div style={lockedField}>
            <Select
              className="form-control rounded-3 text-dark bg-white"
              options={this.checklistOptions(seg.checklist_id)}
              value={seg.checklist_id}
              onChange={(value) => this.updateSegment(seg.key, { checklist_id: value })}
              placeholder={t("cp_select_checklist")}
              disabled={locked}
            />
          </div>
          <div className="text-12 text-secondary mt-1">{formatText(t("cp_seg_checklist_hint"), { index: no })}</div>
          {this.renderChecklistPreview(seg.checklist_id)}

          {this.renderSegmentSchedule(no, start, seg.end_date, rounds, inactiveDate)}
        </div>
      </div>
    );
  }

  // ตัวอย่างตารางงานของแผนย่อย: แท็กเดือน "ต.ค. 2569 | 13 วัน" (เดือนที่ได้ 0 วัน = พื้นขาว) + รวมรอบเข้าพบ
  renderSegmentSchedule(no, start, end, rounds, inactiveDate) {
    return (
      <div className="rounded-4 p-3 mt-3 bg-white" style={{ border: "1px solid #eef0f2" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-bold text-13 text-dark">{formatText(t("cp_seg_schedule_title"), { index: no })}</div>
          <span className="badge rounded-pill text-12 fw-bold px-3 py-1" style={{ backgroundColor: "#dbe8ff", color: "#1e4fc4" }}>
            {formatText(t("cp_seg_visits"), { count: formatNumber(rounds.total) })}
          </span>
        </div>
        {rounds.months.length === 0 ? (
          <div className="text-12 text-secondary">{t("cp_seg_need_end")}</div>
        ) : (
          <div className="d-flex flex-wrap gap-2">
            {rounds.months.map((m) => {
              const empty = m.count === 0;
              return (
                <span
                  key={m.ym}
                  className="d-inline-flex align-items-center rounded-pill text-12 fw-bold px-3 py-1"
                  style={empty ? { backgroundColor: "#fff", border: "1px solid #e5e7eb", color: "#9aa4b2" } : { backgroundColor: "#e0761b", border: "1px solid #e0761b", color: "#fff" }}
                >
                  {monthShortLabel(m.ym)}
                  <span className="mx-2" style={{ width: 1, alignSelf: "stretch", backgroundColor: empty ? "#e5e7eb" : "rgba(255,255,255,0.55)" }}></span>
                  {formatNumber(m.count)} {t("cp_seg_days_unit")}
                </span>
              );
            })}
          </div>
        )}
        <div className="rounded-2 px-3 py-2 mt-3 text-12" style={{ backgroundColor: "#f3f6fb", borderLeft: "3px solid #2f6fed", color: "#3a4451" }}>
          {formatText(t("cp_seg_schedule_note"), { start: fmtDate(start), end: fmtDate(end) })}
          {inactiveDate && <div className="mt-1">{formatText(t("cp_seg_schedule_note_inactive"), { date: fmtDate(inactiveDate) })}</div>}
        </div>
      </div>
    );
  }

  // รายการตรวจสอบของเทมเพลต: เลขเรียง 3 คอลัมน์ ไม่มีหัวคอลัมน์ เรียงซ้ายไปขวาทีละแถว
  renderChecklistPreview(checklistUid) {
    const ck = this.state.data_checklists.find((x) => x.uid === checklistUid);
    if (!ck) return null;
    const items = ck.items || [];
    return (
      <div className="rounded-3 p-3 mt-3" style={{ backgroundColor: "#f7f9fb", border: "1px solid #eef0f2" }}>
        <div className="fw-bold text-13 text-dark">{t("cp_checklist_preview")} ({items.length} {t("cp_items_unit")})</div>
        {ck.description && <div className="text-12 text-secondary mb-2">{ck.description}</div>}
        <div className="mt-2" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", columnGap: 16, rowGap: 6 }}>
          {items.map((item, i) => (
            <div className="text-12 text-dark d-flex gap-2" key={item.uid || i}>
              <span className="text-secondary fw-bold" style={{ width: 22, flexShrink: 0 }}>{i + 1}.</span>
              <span>{item.description || "-"}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
}
